export type { Language } from "../types";

export const TRANSLATIONS = {
  en: {
    // Magic Bar
    structure: "Structure",
    expand: "Expand",
    expand_modal: "Expand Modal",
    collapse_modal: "Collapse Modal",
    condense: "Condense",
    refine: "Refine",
    chat: "Chat",
    info: "Info",
    translate: "Translate",
    translating: "Translating...",
    open_origin_chat: "Open origin chat",
    chat_unavailable: "Origin chat unavailable",
    number_scenes: "# Scenes",

    // Navigation
    back: "Back",

    // Landing / Import
    start_script: "Start Script",
    import_title: "Import Script",
    import_error_title: "Import Error",
    methodology_error_title: "Methodology Error",
    methodology_format_error: "The content provided does not match the accepted format for a methodology.",
    import_error_msg: "The file you are trying to import does not contain a script in the commonly accepted format.",
    import_desc: "Upload .txt or .pdf file",
    paste_title: "Paste Script",
    paste_desc: "Edit raw text directly",
    or_paste: "Or paste raw text",
    paste_placeholder: "Paste your story's synopsis and/or beat sheet here",
    literal: "LITERAL",
    creative: "CREATIVE",
    cancel: "Cancel",
    tag_placeholder: "Add and press comma...",
    tag_error: "Proper names or character names are not allowed as tags.",
    load_script: "Load Script",

    // Ideation
    ideation_title: "New Script",
    ideation_desc: "Brainstorm with AI",
    ideation_header: "Ideation Studio",
    generate_script: "Generate Script",
    generate_new_script: "Generate New Version",
    generate_script_locked: "Discuss story elements to unlock",
    script_ready_msg:
      "You have enough to create a script. Click the 'Generate Script' button above or continue chatting until you feel ready.",
    generating: "Generating...",
    importing: "Importing...",
    generating_animation: "Crafting Screenplay...",
    importing_script: "Importing Script...",
    wait_message: "Please wait. Do not close this screen until finished.",
    input_placeholder: "Start your story by introducing at least one character and a conflict",
    improve_prompt: "Improve Prompt",
    improving: "Improving...",

    // Script Versions
    script_version: "Draft",
    scene: "SCENE",
    of: "OF",
    skip: "skip",
    finishing: "finishing...",
    rename: "Rename",
    delete: "Delete",
    delete_script_confirm: "Delete this script version?",
    delete_version_title: "Delete Script Version",
    delete_version_msg:
      "Are you sure you want to delete this version? This action cannot be undone.",
    export: "Export",
    export_pdf: "Export (.PDF)",
    trial_limit_scripts: "Limit reached: 24 Scripts",
    trial_limit_reached:
      "Trial Version Limit Reached (Max 6 Projects). Please delete an old project to create a new one.",
    trial_limit_reached_title: "Trial Version Limit Reached (Max 6 Projects)",
    trial_limit_reached_body: "Please delete an old project to create a new one.",
    version_limit_reached: "Version Limit Reached. Please delete old versions.",
    applying_corrections: "Applying corrections...",
    applied_successfully: "Script updated successfully!",
    error_generate_version: "Failed to generate new script version.",
    update_script: "Generate New Version",
    updating_script: "Generating...",

    // Text Selection & Editing
    quote_in_chat: "Quote in Chat",
    edit_block: "Edit Paragraph",
    customize_character: "Customize Character",
    edit_dialogue: "Edit Dialogue",
    improve_dialogue: "Improve Dialogue",
    improve_dialogue_title: "Improve Dialogue",
    edit_modal_title: "Edit Block",
    rewrite_ai: "Rewrite with AI",
    rewriting: "Rewriting...",
    apply: "Apply",
    apply_corrections: "Apply Corrections",
    apply_suggestion: "Apply Suggestion",
    regenerate: "Re-analyze",
    original: "Original",

    // Footer
    pro_license: "Pro License",
    tutorial_title: "Quick Start Guide",
    tutorial_step_1_title: "Ideation Studio",
    tutorial_step_1_desc:
      "Start by chatting with the AI. Discuss your premise, characters, or scenes. When ready, click 'Generate Script' to create the initial draft.",
    tutorial_step_2_title: "Smart Editor",
    tutorial_step_2_desc:
      "Edit directly on the page. Use [Enter] to change elements and [Tab] to cycle types (Action > Character > Dialogue).",
    tutorial_step_3_title: "Production Bridge",
    tutorial_step_3_desc:
      "Open the 'Prompts' tab on the right to generate AI image and video prompts based on your scene context.",
    tutorial_step_4_title: "Structural Analysis",
    tutorial_step_4_desc:
      "Use the 'Config' tab to enable McKee or Field methodologies and analyze your pacing in real-time.",
    close: "Close",

    // Editor Help
    editor_help_title: "How to use the editor",
    editor_help_intro: "Select a paragraph to reveal two functions:",
    editor_help_quote:
      "• Click 'Quote in Chat' to create a new script version inspired by new input or AI suggestions",
    editor_help_edit:
      "• Click 'Edit Paragraph' to change it directly. This does not create a new version.",

    // Detailed Help Popup
    help_editor_body_title: "Script Body",
    help_editor_body_1: "Click Dialogue to improve it.",
    help_editor_body_2: "Click Paragraph to Edit it.",
    help_editor_body_3: "Click Categorize to add new Elements",
    help_toolbar_title: "Toolbar",
    help_toolbar_1: "add/edit 27 Pillars - when edited generates new script",
    help_toolbar_2: "analyzes the script",
    help_sidebar_title: "Right Column",
    help_sidebar_elements: "edit characters, locations, and props",
    help_sidebar_actions_ai: "analyzes script and generates a new one",
    help_sidebar_actions_kb: "applies screenwriting methods",
    help_sidebar_actions_analysis: "script structure options",
    help_sidebar_prompts_prod: "generates prompt for each scene",
    help_sidebar_prompts_duration: "calculates story time for video",

    // Sidebar Left (Projects & Scenes)
    projects: "Projects",
    new_project: "New Project",
    rename_current: "Rename Current",
    delete_current: "Delete Current",
    scenes: "Scenes",
    no_scenes: "No scenes in current script",
    pacing: "PACING",
    already_audited: "Audit Completed",
    already_audited_desc: "This version has already been audited. Results are shown below.",
    delete_modal_title: "Delete Project",
    delete_modal_body: "Are you sure you want to delete",
    delete_modal_warning: "This action cannot be undone.",
    confirm: "Confirm",
    untitled: "Untitled",

    // Sidebar Right (Tabs)
    elements: "Elements",
    actions: "Actions",
    prompts: "Prompts",
    config: "SETTINGS",

    // Sidebar Right (Actions/Config)
    knowledge_base: "Knowledge Base",
    synergy_mode: "Synergy Mode Active",
    method_mckee: "Method: R. McKee",
    method_mckee_desc: "Values, Archplot, The Gap.",
    method_field: "Method: Syd Field",
    method_field_desc: "Paradigma, 3 Acts, Plot Points.",
    analysis_tools: "Content Guidelines",
    toast_limit_title: "Focus Limit Reached",
    toast_limit_message: "If you want to avoid giving birth to a narrative Frankenstein, do yourself a favor and stick to two methodologies at most, or just pick the one that actually fits the script you’re trying to cook up.",
    editor_settings: "Editor Settings",
    visual_atmosphere: "Visual Atmosphere",
    visual_atmosphere_desc: "Generate mood and lighting cues.",
    character_arcs: "Character Arcs",
    character_arcs_desc: "Track emotional progression.",
    plant_and_payoff: "Plant & Payoff",
    plant_and_payoff_desc: "Narrative cohesion engineering.",
    pan_with_space: "Pan with Spacebar",
    pan_with_space_desc: "Hold Space and drag to move the page.",
    line_pagination: "Line Pagination",
    line_pagination_desc: "Show line numbers on the editor.",

    custom_knowledge: "Custom Knowledge",
    upload_methodology: "Upload your Methodology",
    custom_method_desc: "Custom uploaded method",
    loading_methodology: "Loading Methodology...",
    drop_files: "Drop files here",

    // Sidebar Right (Actions/Tasks)
    ai_tasks: "AI Tasks",
    task_pacing: "Pacing Analysis",
    task_pacing_desc: "Identify slow/fast scenes.",
    task_camera: "Camera Direction",
    task_camera_desc: "Suggest plans, angles, focus and movements per scene.",
    task_effects: "SFX/VFX Effects",
    task_effects_desc: "Suggest soundFX and effects.",
    task_effects_apply: "Generate New Version",
    task_camera_level: "Complexity Level",
    task_camera_essential: "Essential",
    task_camera_pro: "Professional",
    task_camera_auteur: "Auteur/Cinematic",
    task_camera_filter_toggle: "Apply only to scenes without camera directions",
    task_camera_intent: "Intention",
    task_camera_suggestions: "Suggestions",
    task_camera_justification: "Justification",
    task_camera_apply_btn: "Generate New Version",
    task_camera_ignore_scene: "Ignore camera suggestion",
    task_elements: "New Elements",
    task_elements_desc: "Suggest characters, props, or locations.",
    task_dialogue: "Improve Dialogue",
    task_dialogue_desc: "Refine subtext and naturalism.",
    new_badge: "NEW",
    task_tone_analyzer: "Tone Analyzer",
    task_tone_analyzer_desc: "Analyze voice consistency and genre alignment.",
    task_tone_analyzer_apply_prompt: "Do you want to apply these tonal adjustments?",
    task_tone_analyzer_run: "Analyze Tone",
    tooltip_task_camera: "Logic: Literary to Visual translation. Foundation: Transposes dramatic intent into technical shots (angles, movements, and framing).",
    tooltip_task_elements: "Logic: Depth engineering. Foundation: Scans for gaps in the story's DNA to suggest characters, props, or locations that reinforce the theme.",
    tooltip_task_effects: "Logic: Atmosphere decoding. Foundation: Extracts SFX and VFX suggestions from the subtext that amplify the experience without inventing elements outside the text.",
    tooltip_task_pacing: "Logic: Script Doctor focused on narrative cadence. Foundation: Identifies the ideal rhythm for the genre's central emotion, eliminating dead time or protecting silences.",
    tooltip_task_tone_analyzer: "Logic: Acts as a Tone and Voice consultant. Foundation: Ensures tonal consistency, genre alignment, and differentiation of character voices.",
    tooltip_task_scriptaudit: "Logic: Simulates a professional Script Reader. Foundation: Focuses on 'Master Scenes' formatting, distinguishing style from technical errors that hinder filming.",
    tooltip_task_transitions: "Logic: Cinematic stream of consciousness. Foundation: Suggests thematic or visual connections between scenes to maintain audience immersion.",
    tooltip_task_plottwist: "Logic: Recontextualization of the past. Foundation: A revelation that alters the meaning of what was seen, respecting genre laws and dramatic impact.",
    tooltip_task_plotaudit: "Logic: Causality Audit (A -> B). Foundation: Verifies if events occur by dramatic necessity or convenience, detecting logic and motivation holes.",
    tooltip_task_continuity: "Logic: Logical-temporal rigor. Foundation: Scans the text for inconsistencies in objects, time, and world rules across scenes.",
    tooltip_task_chararcs: "Logic: Journey from Lie to Truth. Foundation: Analyzes emotional transformation, comparing what the character wants vs. what they actually need.",
    tooltip_task_char_diagnostic: "Audits consistency between character profiles and their actions in the script.",
    tooltip_task_visual_atmosphere: "Logic: Visual Concept. Foundation: Translates dramatic subtext into light, color, and texture to create an immersive atmosphere.",
    tooltip_task_plant_and_payoff: "Logic: Narrative Engineering. Foundation: Audits causality across acts and scenes to ensure all narrative seeds have a meaningful resolution.",
    tooltip_task_script_review: "Logic: Master Scene Reconstruction. Foundation: Rewrites the script to enforce standard formatting, automatically fixing issues like Action inside Dialogue.",
    task_transitions: "Transitions",
    task_transitions_desc: "Suggest cuts and fades.",
    task_plottwist: "Plot Twist",
    task_plottwist_desc: "Structural recontextualization.",
    task_plotaudit: "Plot Audit",
    task_plotaudit_desc: "Detect plot holes and logical gaps.",
    task_scriptaudit: "Audit Script",
    task_scriptaudit_desc: "Apply 'script doctor' to evaluate and fix formatting.",
    task_scriptaudit_run: "Diagnose Script",
    perfect_structure: "100/100. Flawless structure. Now the drama is only in the story.",
    task_script_review: "Script Review",
    task_script_review_desc: "Apply 'script doctor' to fix formatting.",
    task_script_review_run: "Fix Script",
    causality_chain: "Causality Chain",
    missing_trigger: "Missing Trigger",
    plot_holes: "Plot Holes",
    fix: "Fix",
    strategic_suggestions: "Strategic Suggestions",
    solve_plot_hole: "Solve Plot Hole",
    choose_solution: "Choose a strategic solution",
    action_beat: "Action Beat",
    insertion_point: "Insertion Point",
    ripple_effect: "Ripple Effect",
    inject_in_scene: "Inject in Scene",
    task_plotaudit_apply_prompt: "Do you want to generate a new script applying these structural corrections?",
    select_all_plot_fixes: "Please select an option (or reject) for each plot hole before applying.",
    no_fixes_selected: "No fixes selected to apply.",
    reject_suggestions: "Reject Suggestions",
    generating_suggestions: "Generating Suggestions...",
    strategic_suggestions_plural: "Strategic Suggestions",
    fix_option: "Option",
    insertion: "Insertion",
    ripple_effect_label: "Effect",
    task_continuity: "Verify Continuity",
    task_continuity_desc:
      "Fix consistency of time, objects, and world rules.",
    task_char_diagnostic: "Character Diagnostic",
    task_char_diagnostic_desc: "Audit consistency between profiles and script.",
    task_char_diagnostic_btn: "Resolve Inconsistency",
    char_diagnostic_running: "ANALYZING...",
    char_diagnostic_suggestions: "Suggestions:",
    char_diagnostic_change_script: "Change Script",
    char_diagnostic_update_profile: "Update Profile",
    char_diagnostic_select_option: "Please select at least one option to apply.",
    char_diagnostic_discrepancy_prompt:
      "Script indicates [X], while Profile indicates [Y]. What do you want?",
    msg_no_bible: "No characters found in the database (The Bible).",
    msg_no_continuity_issues: "No inconsistencies detected.",
    inconsistency_detected: "INCONSISTENCY DETECTED!",
    inconsistency_instruction: "Click on Character Diagnostic to change script or update profile.",
    task_chararcs: "Character Arcs",
    task_chararcs_desc: "Analyze emotional transformation (Lie vs Truth).",
    task_visual_atmosphere: "Visual Atmosphere",
    task_visual_atmosphere_desc: "Rewrite script with mood and lighting cues.",
    task_plant_and_payoff: "Plant & Payoff",
    task_plant_and_payoff_desc: "Narrative cohesion engineering.",
    literary_scripts: "TO APPLY IN LITERARY SCRIPTS",
    technical_scripts: "TO CREATE TECHNICAL SCRIPTS",
    analyzing: "Analyzing...",
    enable_tasks_msg: "Generate a script to enable tasks",
    change_log: "Change Log",
    no_changes: "No corrections needed. Script is clean.",
    no_changes_title: "IMPECCABLE SCRIPT",

    // Sidebar Right (Prompts)
    production_bridge: "Prompt Assistant",
    select_scene: "SELECT SCENE",
    select_shot: "SELECT SHOT",
    select_shot_placeholder: "Select a shot...",
    gen_visual_prompts: "Generate Visual Prompts",
    gen_shot_list: "Generating Prompts",
    no_shots_generated: "No shots generated.",
    import_to_enable: "Import a script to enable generation",
    ai_director: "AI Director Standby",
    ai_director_desc:
      "Select a scene and a shot/plan (optional). Then click Generate... to create image and video prompts for the entire scene or for the selected shot.",
    export_prompts: "Export Prompts",
    edit_prompt: "Edit",
    save_prompt: "Save",
    prompt_results_title: "Prompt Generation Results",
    image_prompts: "IMAGE PROMPTS",
    video_prompts: "VIDEO PROMPTS",
    shot_label: "SHOT",
    ai_engine_status: "AI PRODUCTION ENGINE • STANDBY",
    footer_tag: "AUTEUR OPERATING SYSTEM — v3.1 PROMPT ENGINE",
    visual_action_processing: "Visual action processing...",
    select_scene_to_reveal: "Select a scene to reveal visual prompts",
    cinematic_shot: "CINEMATIC SHOT",
    view_results: "View Results",
    prompts_generated: "Prompts Generated",
    ai_director_standby: "AI DIRECTOR STANDBY",
    select_scene_shot_warning: "SELECT SCENE/SHOT",

    // Duration Estimation
    duration_estimate: "Duration Estimate",
    estimating: "Estimating...",
    duration_total: "Total Duration",
    probable_range: "Probable Range",
    pacing_slider: "Rhythm Adjustment",
    slow: "Slow",
    fast: "Fast",
    normal: "Normal",
    scene_breakdown: "Scene Breakdown",
    classification: "Format",
    reasoning: "Justification",
    minutes: "min",
    estimated_total_duration: "Estimated Runtime",
    fast_pacing: "Faster Pacing",
    slow_pacing: "Slower Pacing",
    ai_analysis: "AI Analysis",
    scene_slug: "SCENE / SLUG",
    time: "TIME",

    // Sidebar Right (Elements)
    characters: "Characters",
    no_characters: "No characters detected yet.",
    no_description: "No description available.",

    locations: "Locations",
    no_locations: "No locations detected yet.",
    props: "Props",
    no_props: "No props detected yet.",
    lines: "Lines",
    char_name: "Character Name",
    prop_name: "Prop Name",
    loc_name: "Location Name",
    char_desc: "Description / Profile",
    save: "Save",
    apply_to_script: "Apply to script",
    improve_with_ai: "Improve with AI",
    no_sfx: "No SFX/VFX detected yet.",
    no_camera: "No camera directions detected yet.",
    no_transitions: "No transitions detected yet.",
    go_to_scene: "Go to scene:",

    // Story Pillars
    pillars_title: "Story Pillars",
    pillars: {
      protagonist: {
        name: "Protagonist",
        description: "Central character who drives the narrative.",
      },
      objective: {
        name: "Goal",
        description: "Clear and measurable goal of the protagonist.",
      },
      antagonist: {
        name: "Antagonist / Opposing Force",
        description: "Force that prevents the protagonist's progress.",
      },
      conflict: { name: "Conflict", description: "Main tension of the story." },
      stakes: {
        name: "Stakes",
        description: "What is at risk if the protagonist fails.",
      },
      setting: {
        name: "Setting / Era",
        description: "Where and when the story takes place.",
      },
      theme: {
        name: "Theme",
        description: "Central idea or argument of the work.",
      },
      internal_desire: {
        name: "Internal Desire",
        description: "Emotional need of the protagonist.",
      },
      tragic_flaw: {
        name: "Tragic Flaw",
        description: "Defect that generates conflict.",
      },
      arc: {
        name: "Character Arc",
        description: "Transformation throughout the story.",
      },
      inciting_incident: {
        name: "Inciting Incident",
        description: "Event that starts the journey.",
      },
      plan: {
        name: "Protagonist's Plan",
        description: "Strategy to achieve the goal.",
      },
      obstacles: {
        name: "Progressive Obstacles",
        description: "Increasing difficulties.",
      },
      no_return: {
        name: "Point of No Return",
        description: "Irreversible decision.",
      },
      climax: { name: "Climax", description: "Maximum confrontation." },
      resolution: { name: "Resolution", description: "New final balance." },
      motif: {
        name: "Recurring Motif",
        description: "Repeated symbolic element.",
      },
      irony: {
        name: "Dramatic Irony",
        description: "Contrast between intention and result.",
      },
      moral_cost: {
        name: "Moral Rule / Ethical Cost",
        description: "Price of winning.",
      },
      genre_promise: {
        name: "Genre Promise",
        description: "Expectation created in the audience.",
      },
      tone: {
        name: "Tone",
        description: "Emotional attitude of the narrative.",
      },
      pacing: {
        name: "Pacing / Escalation",
        description: "Progression of tension.",
      },
      trigger: {
        name: "Emotional Trigger",
        description: "Main emotion provoked in the audience.",
      },
      plant_payoff: {
        name: "Plant & Payoff",
        description: "Narrative preparation and reward.",
      },
      pov: {
        name: "Narrative Point of View",
        description: "Who leads emotionally.",
      },
      world_conflict: {
        name: "World in Conflict",
        description: "How the world reacts.",
      },
      ending_feeling: {
        name: "Emotional Ending",
        description: "Feeling left in the audience.",
      },
    },
    pillar_protagonist_placeholder: "Who is the hero? (Name, Age, Role)",
    pillar_goal_placeholder: "What do they want? (Tangible objective)",
    pillar_antagonist_placeholder:
      "Who/what prevents the protagonist? [person, system, nature, self]",
    pillar_conflict_placeholder: "Central dramatic conflict dynamics.",
    pillar_stakes_placeholder:
      "What do they lose if they fail? External and internal consequences.",
    pillar_setting_placeholder: "Where and When? (World rules)",
    apply_pillars: "Apply Pillars",
    auto_suggest: "Auto-Suggest",
    define_pillars: "Define Story Pillars",
    analyzing_pillars: "Analyzing Script DNA...",
    detected_format: "Detected Format",
    format_film: "Film",
    format_animation: "Animation",
    format_documentary: "Documentary",
    format_advertisement: "Commercial",
    add_pillars: "Add Pillars",
    add_to_chat_btn: "Add to Chat",
    regenerate_all: "Regenerate All",

    // Project Creation Modal
    start_script_modal_title: "Start New Script",
    edit_title_page: "Edit Title Page",
    project_title_label: "Provisional Title",
    author_label: "Author Name",
    based_on_label: "Based on, If Any",
    contact_info_label: "E-mail / Phone Number",
    written_by_label: "Written by",
    create_project_btn: "Create Project",
    recategorize: "Recategorize",
    move_to_existing: "Move to existing category",
    create_new_category: "Create new category",
    new_category_name: "New category name",
    category: "Category",
    ignore_element: "Ignore Element",
    elements_footer_msg: "Generate a script to enable elements",
    auth_errors: {
      invalid_email: "Invalid email format.",
      user_disabled: "This user account has been disabled.",
      user_not_found: "Email or password incorrect.",
      wrong_password: "Email or password incorrect.",
      email_already_in_use: "This email is already in use. Try logging in instead.",
      account_exists_with_different_credential: "An account already exists with this email using a different login method.",
      invalid_credential: "Email or password incorrect.",
      weak_password: "The password is too weak (min 6 characters).",
      operation_not_allowed: "Sign-in operation not allowed.",
      too_many_requests: "Too many failed attempts. Try again later.",
      default: "An unexpected error occurred. Please try again.",
    },
    email: "Email",
    confirm_email: "Confirm Email",
    password: "Password",
    email_mismatch: "Emails do not match.",
    forgot_password_question: "Forgot your password?",
    no_account_link: "Don't have an account?",
    signup_now: "Sign up",
    already_have_account_link: "Already have an account?",
    login_now: "Sign in",
    check_email_title: "Check your email",
    recovery_sent_msg: "We sent a recovery link to:",
    back_to_login: "Back to Login",
    send_link: "Send Link",

    // Character Modal
    char_modal_title: "Character Profile",
    dramatic_function: "Dramatic Function",
    char_name_placeholder: "CHARACTER NAME",
    identity: "Identity",
    identity_fullname_placeholder: "Ex: John Doe",
    identity_age_placeholder: "Ex: 35 years old",
    identity_profession_placeholder: "Ex: Private Detective",
    identity_social_class_placeholder: "Ex: Middle Class",
    identity_era_placeholder: "Ex: London, 1890",
    identity_location_placeholder: "Ex: Baker Street",
    psychology: "Psychology",
    psychology_archetype_placeholder: "Ex: Reluctant Hero",
    psychology_moral_placeholder: "Describe values and principles...",
    psychology_fears_placeholder: "What the character fears...",
    psychology_goal_placeholder: "Main motivation in the story...",
    expression: "Expression",
    expression_speech_placeholder: "Ex: Formal, witty, sarcastic...",
    expression_humor_placeholder: "Ex: Dark humor, ironic, none...",
    expression_visual_placeholder: "Detailed prompt for image generation...",
    full_name: "Full Name",
    age: "Age",
    profession: "Profession/Role",
    social_class: "Social Class",
    era_setting: "Era/Setting",
    location: "Location",
    real_person: "Real Person?",
    archetype: "Archetype",
    personality_traits: "Personality Traits",
    moral_code: "Moral Code / Stance",
    fears: "Fears / Pains",
    primary_goal: "Primary Goal",
    speech_style: "Speech Style",
    topics: "Frequent Topics",
    humor: "Sense of Humor",
    visual_desc: "Visual Description (Avatar Prompt)",
    save_char: "Save Character",
    role_protagonist: "Protagonist",
    role_antagonist: "Antagonist",
    role_supporting: "Supporting",
    role_extra: "Extra",
    role_interviewee: "Interviewee",
    role_narrator: "Narrator",
    role_mascot: "Mascot",
    import_characters: "Import Characters",
    import_characters_desc: "Extract profiles from script",
    char_modal_extract: "CHARACTER EXTRACTION",
    analysis_complete_verify: "Analysis Complete - Verify details",
    all_chars_have_profiles: "All detected characters already have profiles.",
    ai_extraction_results: "Identified Characters",
    review_candidates: "Select the characters you want to import.",
    deselect_all: "Deselect All",
    select_all: "Select All",
    confirm_import: "Import",
    character_profile_not_extracted: "Profile not extracted yet. Fill manually or use Automatic Extraction in the sidebar.",
    char_extracting: "EXTRACTING CHARACTER",
    auto_complete_profile: "✨ Auto-Complete Profile",
    profile_auto_filled: "Profile of {name} filled by AI.",

    // Audit
    technical_error: "TECHNICAL ERROR",
    stylistic_suggestion: "STYLISTIC SUGG.",
    what_is_it: "What is it?",
    technical_error_tip: "Violations of Master Scenes rules that prevent the script from being filmable or professional (deducts points)",
    stylistic_suggestion_tip: "Suggestions for subtext and creative refinement (does NOT affect the technical score)",
    selected: "selected",
    ignore: "Ignore",
    applied: "Applied",
    ignored: "Ignored",
    suggestions_found: "Refinement suggestions found.",
    no_errors_found: "No technical errors found in this round.",

    // Round / Status
    final_round: "Final Round",
    round_label: "Round",
    verified: "Verified",

    // Character Diagnostic Paths
    path_a: "Path A: Adjust Script",
    path_b: "Path B: Update Profile",
    or: "Or...",

    // Settings
    mature_language: "Allow Mature Language",
    mature_language_desc: "Enable unfiltered content (profanity, violence, adult themes).",
    horror_mode: "Horror & Violence Mode",
    horror_mode_desc: "Enable extreme tension, gore, and visceral descriptions.",
  },
  pt: {
    structure: "Estrutura",
    expand: "Expandir",
    expand_modal: "Expandir Modal",
    collapse_modal: "Reduzir Modal",
    condense: "Resumir",
    refine: "Refinar",
    chat: "Chat",
    info: "Info",
    translate: "Traduzir",
    translating: "Traduzindo...",
    open_origin_chat: "Abrir chat de origem",
    chat_unavailable: "Chat de origem indisponível",
    number_scenes: "# Cenas",
    back: "Voltar",
    import_title: "Importar Roteiro",
    import_error_title: "Erro de Importação",
    methodology_error_title: "Erro de Metodologia",
    methodology_format_error: "O conteúdo fornecido não está no padrão aceito para metodologia.",
    import_error_msg: "O arquivo que você está tentando importar não contém um roteiro no padrão comumente aceito.",
    import_desc: "Upload de arquivo .txt ou .pdf",
    paste_title: "Colar Roteiro",
    paste_desc: "Edite texto puro diretamente",
    or_paste: "Ou cole texto puro",
    paste_placeholder: "Cole aqui a sinopse e ou a escaleta da sua história",
    literal: "LITERAL",
    creative: "CRIATIVO",
    cancel: "Cancelar",
    tag_placeholder: "Adicione e pressione vírgula...",
    tag_error: "Nomes próprios ou nomes de personagens não são permitidos como tags.",
    load_script: "Carregar Roteiro",
    start_script: "Iniciar Roteiro",
    ideation_title: "Novo Roteiro",
    ideation_desc: "Brainstorm com IA",
    ideation_header: "Estúdio de Ideação",
    generate_script: "Gerar Roteiro",
    generate_new_script: "Gerar Nova Versão",
    generate_script_locked: "Discute a história para desbloquear",
    script_ready_msg:
      "Você já tem o suficiente para criar um roteiro, clique no botão acima Gerar Roteiro ou continue com o chat até achar que tem um roteiro para começar.",
    generating: "Gerando...",
    importing: "Importando...",
    generating_animation: "Escrevendo Roteiro...",
    importing_script: "Importando Roteiro...",
    wait_message: "Aguarde. Não feche esta tela até finalizar.",
    input_placeholder: "Comece sua história apresentando ao menos um personagem e um conflito",
    improve_prompt: "Melhorar Prompt",
    improving: "Melhorando...",

    // Script Versions
    script_version: "Versão",
    scene: "CENA",
    of: "DE",
    skip: "pular",
    finishing: "finalizando...",
    rename: "Renomear",
    delete: "Excluir",
    delete_script_confirm: "Deletar esta versão do roteiro?",
    delete_version_title: "Deletar Versão do Roteiro",
    delete_version_msg:
      "Tem certeza que deseja deletar? Você pode exportar antes.",
    export: "Exportar",
    export_pdf: "Exportar (.PDF)",
    trial_limit_scripts: "Limite atingido: 24 Roteiros",
    trial_limit_reached:
      "Limite da Versão de Teste Atingido (Máx 6 Projetos). Por favor, apague um projeto antigo para criar um novo.",
    trial_limit_reached_title: "Limite da Versão de Teste Atingido (Máx 6 Projetos)",
    trial_limit_reached_body: "Por favor, apague um projeto antigo para criar um novo.",
    version_limit_reached:
      "Limite de Versões Atingido. Por favor, apague versões antigas.",
    applying_corrections: "A aplicar...",
    applied_successfully: "Roteiro atualizado com sucesso!",
    error_generate_version: "Falha ao gerar nova versão do roteiro.",
    update_script: "Gerar Nova Versão",
    updating_script: "Gerando...",

    // Text Selection & Editing
    quote_in_chat: "Citar no Chat",
    edit_block: "Editar Parágrafo",
    customize_character: "Personalizar Personagem",
    edit_dialogue: "Editar Diálogo",
    improve_dialogue: "Melhorar Diálogo",
    improve_dialogue_title: "Melhorar Diálogo",
    edit_modal_title: "Editar Bloco",
    rewrite_ai: "Reescrever com IA",
    rewriting: "Reescrevendo...",
    apply: "Aplicar",
    apply_corrections: "Aplicar Correções",
    apply_suggestion: "Aplicar Sugestão",
    regenerate: "Re-analisar",
    original: "Original",

    pro_license: "Licença Pro",
    tutorial_title: "Guia Rápido",
    tutorial_step_1_title: "Estúdio de Ideação",
    tutorial_step_1_desc:
      "Comece conversando com a IA. Discuta premissas e cenas. Quando pronto, clique em 'Gerar Roteiro' para criar o rascunho inicial.",
    tutorial_step_2_title: "Editor Inteligente",
    tutorial_step_2_desc:
      "Edite na página. Use [Enter] para mudar elementos e [Tab] para alternar tipos (Ação > Personagem > Diálogo).",
    tutorial_step_3_title: "Ponte de Produção",
    tutorial_step_3_desc:
      "Abra a aba 'Prompts' à direita para gerar prompts de imagem e vídeo baseados no contexto da sua cena.",
    tutorial_step_4_title: "Análise Estrutural",
    tutorial_step_4_desc:
      "Use a aba 'Config' para ativar metodologias de McKee ou Field e analisar seu ritmo em tempo real.",
    close: "Fechar",

    // Editor Help
    editor_help_title: "Como usar o editor",
    editor_help_intro: "Selecione um parágrafo para mostrar duas funções:",
    editor_help_quote:
      "• clique em Citar no Chat para criar um novo roteiro inspirado na nova informação digitada ou sugerida por IA",
    editor_help_edit:
      "• clique em Editar Parágrafo para alterá-lo. Essa ação não irá criar um novo roteiro.",

    // Detailed Help Popup
    help_editor_body_title: "Corpo do roteiro",
    help_editor_body_1: "Clique no Diálogo para melhorá-lo.",
    help_editor_body_2: "Clique no Parágrafo para Editá-lo.",
    help_editor_body_3: "Clique em Categorizar para adicionar novos Elementos",
    help_toolbar_title: "Toolbar",
    help_toolbar_1:
      "adiciona/edita 27 Pilares - quando editado gera novo roteiro",
    help_toolbar_2: "faz análise do roteiro",
    help_sidebar_title: "Coluna da direita",
    help_sidebar_elements: "permite editar personagens, locações e adereços",
    help_sidebar_actions_ai: "analisa roteiro e gera um novo",
    help_sidebar_actions_kb: "aplica métodos de roteiro",
    help_sidebar_actions_analysis: "opções de estrutura de roteiro",
    help_sidebar_prompts_prod: "gera prompt para cada cena",
    help_sidebar_prompts_duration: "calcula o tempo da história para vídeo",

    // Sidebar Left
    projects: "Projetos",
    new_project: "Novo Projeto",
    rename_current: "Renombrar Atual",
    delete_current: "Deletar Atual",
    scenes: "Cenas",
    no_scenes: "Nenhuma cena no roteiro atual",
    pacing: "RITMO",
    already_audited: "Versão já Auditada",
    already_audited_desc: "Os resultados abaixo referem-se à análise já realizada nesta versão.",
    delete_modal_title: "Deletar Projeto",
    delete_modal_body: "Tem certeza que deseja deletar",
    delete_modal_warning: "Esta ação não pode ser desfeita.",
    confirm: "Confirmar",
    untitled: "Sem Título",

    // Sidebar Right (Tabs)
    elements: "Elementos",
    actions: "Ações",
    prompts: "Prompts",
    config: "CONFIGURAÇÕES",

    // Sidebar Right (Actions/Config)
    knowledge_base: "Base de Conhecimento",
    synergy_mode: "Modo Sinergia Ativo",
    method_mckee: "Método: R. McKee",
    method_mckee_desc: "Valores, Archplot, O Abismo.",
    method_field: "Método: Syd Field",
    method_field_desc: "Paradigma, 3 Atos, Pontos de Virada.",
    analysis_tools: "Diretrizes de Conteúdo",
    toast_limit_title: "Limite de Foco Atingido",
    toast_limit_message: "Se não quiser parir um Frankenstein narrativo, faça um favor a si mesmo e ative no máximo duas metodologias, ou vá direto na que realmente combina com o roteiro que você quer gestar.",
    editor_settings: "Configurações do Editor",
    visual_atmosphere: "Atmosfera Visual",
    visual_atmosphere_desc: "Gerar clima e dicas de iluminação.",
    character_arcs: "Arcos de Personagem",
    character_arcs_desc: "Rastrear progressão emocional.",
    plant_and_payoff: "Plant & Payoff",
    plant_and_payoff_desc: "Engenharia de coesão narrativa.",
    pan_with_space: "PAN com Barra de Espaço",
    pan_with_space_desc: "Segure Espaço e arraste para mover a página.",
    line_pagination: "Paginação de Linhas",
    line_pagination_desc: "Exibir numeração de linhas no editor.",

    custom_knowledge: "Conhecimento Personalizado",
    upload_methodology: "Subir sua Metodologia",
    custom_method_desc: "Metodologia personalizada carregada",
    loading_methodology: "Carregando Metodologia...",
    drop_files: "Solte os arquivos aqui",

    // Sidebar Right (Actions/Tasks)
    ai_tasks: "Tarefas IA",
    task_pacing: "Análise de Ritmo",
    task_pacing_desc: "Identificar seções lentas/rápidas.",
    task_camera: "Direção de Câmeras",
    task_camera_desc: "Sugerir planos, ângulos, movimentos e foco por cena.",
    task_effects: "SFX/VFX Effects",
    task_effects_desc: "Sugerir soundFX e efeitos.",
    task_effects_apply: "Gerar Nova Versão",
    task_script_review: "Revisar Roteiro",
    task_script_review_desc: "Aplicar 'script doctor' para corrigir formatação.",
    task_script_review_run: "Corrigir Roteiro",
    causality_chain: "Cadeia de Causalidade",
    missing_trigger: "Gatilho não encontrado",
    plot_holes: "Furos de Trama",
    fix: "Consertar",
    strategic_suggestions: "Sugestões Estratégicas",
    solve_plot_hole: "Resolver Furo de Trama",
    choose_solution: "Escolha uma solução estratégica",
    action_beat: "Beat de Ação",
    insertion_point: "Ponto de Inserção",
    ripple_effect: "Efeito Cascata",
    inject_in_scene: "Injetar na Cena",
    change_log: "Histórico de Mudanças",
    no_changes: "Nenhuma correção necessária. Roteiro limpo.",
    no_changes_title: "ROTEIRO IMPECÁVEL",
    task_camera_level: "Nível de Complexidade",
    task_camera_essential: "Essencial",
    task_camera_pro: "Profissional",
    task_camera_auteur: "Autoral/Cinematográfico",
    task_camera_filter_toggle:
      "Aplicar somente em cenas sem direções de câmera",
    task_camera_intent: "Intenção",
    task_camera_suggestions: "Sugestões",
    task_camera_justification: "Justificativa",
    task_camera_apply_btn: "Gerar Nova Versão",
    task_camera_ignore_scene: "Ignorar sugestão de câmera",
    task_elements: "Novos Elementos",
    task_elements_desc: "Sugerir personagens e objetos.",
    task_dialogue: "Melhorar Diálogo",
    task_dialogue_desc: "Refinar subtexto e naturalismo.",
    new_badge: "NOVO",
    task_tone_analyzer: "Analisador de Tom",
    task_tone_analyzer_desc: "Analisa consistência de voz e alinhamento de gênero.",
    task_tone_analyzer_apply_prompt: "Deseja realizar estes ajustes tonais?",
    task_tone_analyzer_run: "Analisar Tom",
    tooltip_task_camera: "Lógica: Tradução do Literário para o Visual. Fundamento: Transpõe a intenção dramática para planos técnicos (angulações, movimentos e enquadramentos).",
    tooltip_task_elements: "Lógica: Engenharia de profundidade. Fundamento: Escaneia lacunas no DNA da história para sugerir personagens, objetos ou locações que reforcem o tema.",
    tooltip_task_script_review: "Lógica: Reconstrução de Master Scenes. Fundamento: Reescreve o roteiro para aplicar a formatação padrão, corrigindo automaticamente problemas como Ação dentro de Diálogo.",
    tooltip_task_effects: "Lógica: Decodificação de atmosfera. Fundamento: Extrai do subtexto sugestões de SFX e VFX que amplificam a experiência sem inventar elementos fora do texto.",
    tooltip_task_pacing: "Lógica: Script Doctor focado em cadência narrativa. Fundamento: Identifica o ritmo ideal para a emoção central do gênero, eliminando tempos mortos ou protegendo silêncios.",
    tooltip_task_tone_analyzer: "Lógica: Atua como consultor de Tom e Voz. Fundamento: Garante consistência tonal, alinhamento com o gênero e diferenciação das vozes dos personagens.",
    tooltip_task_scriptaudit: "Lógica: Simula um Script Reader profissional. Fundamento: Foca na formatação 'Master Scenes', separando o que é estilo do que é erro técnico que impede a filmagem.",
    tooltip_task_transitions: "Lógica: Fluxo de consciência cinematográfico. Fundamento: Sugere conexões temáticas ou visuais entre cenas para manter a imersão do espectador.",
    tooltip_task_plottwist: "Lógica: Recontextualização do passado. Fundamento: Revelação que altera o impacto dramático e o significado do que foi visto, respeitando as leis de gênero.",
    tooltip_task_plotaudit: "Lógica: Auditoria de Causalidade (A -> B). Fundamento: Verifica se os eventos ocorrem por necessidade dramática ou conveniência, detectando furos de lógica e motivação.",
    tooltip_task_continuity: "Lógica: Rigor lógico-temporal. Fundamento: Varre o texto em busca de inconsistências em objetos, tempo e regras do mundo entre as cenas.",
    tooltip_task_chararcs: "Lógica: Jornada da Mentira para a Verdade. Fundamento: Analisa a transformação emocional, comparando o que o personagem quer vs. o que ele realmente precisa.",
    tooltip_task_char_diagnostic: "Audita a consistência entre os perfis dos personagens e suas ações no roteiro.",
    tooltip_task_visual_atmosphere: "Lógica: Conceito Visual. Fundamento: Traduz o subtexto dramático em luz, cor e textura para criar uma atmosfera imersiva.",
    tooltip_task_plant_and_payoff: "Lógica: Engenharia Narrativa. Fundamento: Audita a causalidade entre atos e cenas para garantir que todas as sementes narrativas tenham uma resolução significativa.",
    task_transitions: "Transições",
    task_transitions_desc: "Sugerir cortes e fades.",
    task_plottwist: "Plot Twist",
    task_plottwist_desc: "Recontextualização estrutural.",
    task_plotaudit: "Auditoria de Trama",
    task_plotaudit_desc: "Detectar furos de roteiro e lógica.",
    task_scriptaudit: "Auditar Roteiro",
    task_scriptaudit_desc: "Aplicar 'script doctor' para avaliar e corrigir formatação.",
    task_scriptaudit_run: "Diagnosticar Roteiro",
    perfect_structure: "100/100. Estrutura impecável. Agora o drama é só na história.",
    task_plotaudit_apply_prompt: "Deseja gerar um novo roteiro aplicando essas correções estruturais?",
    select_all_plot_fixes: "Por favor, selecione uma opção (ou rejeite) para cada furo da trama antes de aplicar.",
    no_fixes_selected: "Nenhuma correção selecionada para aplicar.",
    reject_suggestions: "Rejeitar Sugestões",
    generating_suggestions: "Gerando Sugestões...",
    strategic_suggestions_plural: "Sugestões Estratégicas",
    fix_option: "Opção",
    insertion: "Inserção",
    ripple_effect_label: "Efeito",
    task_continuity: "Verificar Continuidade",
    task_continuity_desc:
      "Corrigir consistência de tempo/objetos/regras do mundo.",
    task_char_diagnostic: "Diagnóstico de Personagens",
    task_char_diagnostic_desc: "Audita a coerência entre perfis e roteiro.",
    task_char_diagnostic_btn: "Resolver Inconsistência",
    char_diagnostic_running: "ANALISANDO...",
    char_diagnostic_suggestions: "Sugestões:",
    char_diagnostic_change_script: "Alterar Roteiro",
    char_diagnostic_update_profile: "Atualizar Perfil",
    char_diagnostic_select_option: "Selecione ao menos uma opção para aplicar.",
    char_diagnostic_discrepancy_prompt:
      "Roteiro indica [X], já no Perfil indica... [Y]. Você quer?",
    msg_no_bible: "Nenhum personagem encontrado no banco de dados (The Bible).",
    msg_no_continuity_issues: "Nenhuma descontinuidade detectada.",
    inconsistency_detected: "INCONSISTÊNCIA DETECTADA!",
    inconsistency_instruction: "Clique em Diagnóstico de Personagens para alterar roteiro ou atualizar perfil.",
    task_chararcs: "Arcos de Personagem",
    task_chararcs_desc:
      "Analisar transformação emocional (Mentira vs Verdade).",
    task_visual_atmosphere: "Atmosfera Visual",
    task_visual_atmosphere_desc: "Reescrever cenas com clima e luz.",
    task_plant_and_payoff: "Plant & Payoff",
    task_plant_and_payoff_desc: "Engenharia de coesão narrativa.",
    literary_scripts: "PARA APLICAR EM ROTEIROS LITERÁRIOS",
    technical_scripts: "PARA CRIAÇÃO DE ROTEIROS TÉCNICOS",
    analyzing: "Analisando...",
    enable_tasks_msg: "Gere um roteiro para habilitar as tarefas",

    // Sidebar Right (Prompts)
    production_bridge: "Assistente de Prompts",
    select_scene: "SELECIONAR CENA",
    select_shot: "SELECIONAR PLANO/SHOT",
    select_shot_placeholder: "Selecione um plano/shot...",
    gen_visual_prompts: "Gerar Prompts Visuais",
    gen_shot_list: "Gerando Prompts",
    no_shots_generated: "Nenhum shot gerado.",
    import_to_enable: "Importe um roteiro para habilitar",
    ai_director: "Diretor IA em Espera",
    ai_director_desc:
      "Selecione uma cena e um shot/plano (opcional). Depois clique em Gerar... para criar prompts de imagens e vídeos para a cena inteira ou para o shot selecionado.",
    export_prompts: "Exportar Prompts",
    edit_prompt: "Editar",
    save_prompt: "Salvar",
    prompt_results_title: "Resultados da geração de prompts",
    image_prompts: "PROMPTS DE IMAGEM",
    video_prompts: "PROMPTS DE VÍDEO",
    shot_label: "PLANO",
    ai_engine_status: "MOTOR DE PRODUÇÃO IA • STANDBY",
    footer_tag: "SISTEMA OPERACIONAL AUTEUR — v3.1 PROMPT ENGINE",
    visual_action_processing: "Descrição visual em processamento...",
    select_scene_to_reveal: "Selecione uma cena para exibir os prompts",
    cinematic_shot: "PLANO CINEMATOGRÁFICO",
    view_results: "VER RESULTADOS",
    prompts_generated: "Prompts Gerados",
    ai_director_standby: "DIRETOR IA EM ESPERA",
    select_scene_shot_warning: "SELECIONE A CENA/SHOT",

    // Duration Estimation
    duration_estimate: "Estimativa de Duração",
    estimating: "Estimando...",
    duration_total: "Duração Total",
    probable_range: "Intervalo Provável",
    pacing_slider: "Ajuste de Ritmo",
    slow: "Lento",
    fast: "Rápido",
    normal: "Normal",
    scene_breakdown: "Detalhamento por Cena",
    classification: "Formato",
    reasoning: "Justificativa",
    minutes: "min",
    estimated_total_duration: "Tempo Total Estimado",
    fast_pacing: "Ritmo Rápido",
    slow_pacing: "Ritmo Lento",
    ai_analysis: "Análise de IA",
    scene_slug: "CENA / SLUG",
    time: "TEMPO",

    // Sidebar Right (Elements)
    characters: "Personagens",
    no_characters: "Nenhum personagem detectado.",
    no_description: "Sem descrição disponível.",

    locations: "Locações",
    no_locations: "Nenhuma locação detectada.",
    props: "Adereços",
    no_props: "Nenhum adereço detectado.",
    lines: "Falas",
    char_name: "Nome do Personagem",
    prop_name: "Nome do Adereço",
    loc_name: "Nome da Locação",
    char_desc: "Descrição / Perfil",
    save: "Salvar",
    apply_to_script: "Aplicar no roteiro",
    improve_with_ai: "Melhorar com IA",
    no_sfx: "Nenhum SFX/VFX detectado.",
    no_camera: "Nenhuma direção de câmera detectada.",
    no_transitions: "Nenhuma transição detectada.",
    go_to_scene: "Ir para cena:",

    // Story Pillars
    pillars_title: "Pilares da História",
    pillars: {
      protagonist: {
        name: "Protagonista",
        description: "Personagem central que conduz a narrativa.",
      },
      objective: {
        name: "Objetivo",
        description: "Meta clara e mensurável do protagonista.",
      },
      antagonist: {
        name: "Antagonista / Força Opositora",
        description: "Força que impede o progresso do protagonista.",
      },
      conflict: {
        name: "Conflito",
        description: "Tensão principal da história.",
      },
      stakes: {
        name: "Stakes (O que está em jogo)",
        description: "O que está em jogo se o protagonista falhar.",
      },
      setting: {
        name: "Cenário / Era",
        description: "Onde e quando a história acontece.",
      },
      theme: {
        name: "Tema",
        description: "Ideia central ou argumento da obra.",
      },
      internal_desire: {
        name: "Desejo Interno",
        description: "Necessidade emocional do protagonista.",
      },
      tragic_flaw: {
        name: "Falha Trágica",
        description: "Defeito que gera conflito.",
      },
      arc: {
        name: "Arco do Personagem",
        description: "Transformação ao longo da história.",
      },
      inciting_incident: {
        name: "Incidente Incitante",
        description: "Evento que inicia a jornada.",
      },
      plan: {
        name: "Plano do Protagonista",
        description: "Estratégia para atingir o objetivo.",
      },
      obstacles: {
        name: "Obstáculos Progressivos",
        description: "Dificuldades crescentes.",
      },
      no_return: {
        name: "Ponto de Não Retorno",
        description: "Decisão irreversível.",
      },
      climax: { name: "Clímax", description: "Confronto máximo." },
      resolution: { name: "Resolução", description: "Novo equilíbrio final." },
      motif: {
        name: "Motivo Recorrente",
        description: "Elemento simbólico repetido.",
      },
      irony: {
        name: "Ironia Dramática",
        description: "Contraste entre intenção e resultado.",
      },
      moral_cost: {
        name: "Regra Moral / Custo Ético",
        description: "Preço de vencer.",
      },
      genre_promise: {
        name: "Promessa de Gênero",
        description: "Expectativa criada no público.",
      },
      tone: { name: "Tom", description: "Atitude emocional da narrativa." },
      pacing: {
        name: "Ritmo / Escalada",
        description: "Progressão da tensão.",
      },
      trigger: {
        name: "Gatilho Emocional",
        description: "Emoção principal provocada no público.",
      },
      plant_payoff: {
        name: "Plant & Payoff",
        description: "Preparação e recompensa narrativa.",
      },
      pov: {
        name: "Ponto de Vista Narrativo",
        description: "Quem conduz emocionalmente.",
      },
      world_conflict: {
        name: "Mundo em Conflito",
        description: "Como o mundo reage.",
      },
      ending_feeling: {
        name: "Final Emocional",
        description: "Sensação deixada no público.",
      },
    },
    pillar_protagonist_placeholder: "Quem é o herói? (Nome, Idade, Papel)",
    pillar_goal_placeholder: "O que ele quer? (Objetivo tangível)",
    pillar_antagonist_placeholder:
      "Quem/que força impede o protagonista? [pessoa, sistema, natureza, ele mesmo]",
    pillar_conflict_placeholder: "Dinâmica do conflito central dramático.",
    pillar_stakes_placeholder:
      "O que ele perde se falhar? Consequências externas e internas",
    pillar_setting_placeholder: "Onde e Quando? (Regras do mundo)",
    apply_pillars: "Aplicar Pilares",
    auto_suggest: "Auto-Sugerir",
    define_pillars: "Definir Pilares",
    analyzing_pillars: "Analisando DNA do Roteiro...",
    detected_format: "Formato Detectado",
    format_film: "Filme",
    format_animation: "Animação",
    format_documentary: "Documentário",
    format_advertisement: "Comercial",
    add_pillars: "Adicionar Pilares",
    add_to_chat_btn: "Adicionar no Chat",
    regenerate_all: "Regenerar Tudo",

    // Project Creation Modal
    start_script_modal_title: "Iniciar Novo Roteiro",
    edit_title_page: "Editar Página de Título",
    project_title_label: "Título Provisório",
    author_label: "Nome do(a) Autor(a)",
    based_on_label: "Baseado em, Se houver",
    contact_info_label: "E-mail / Telefone",
    written_by_label: "Escrito por",
    create_project_btn: "Criar Projeto",
    recategorize: "Recategorizar",
    move_to_existing: "Mover para categoria existente",
    create_new_category: "Criar nova categoria",
    new_category_name: "Nome da nova categoria",
    category: "Categoria",
    ignore_element: "Ignorar Elemento",
    elements_footer_msg: "Gere um roteiro para habilitar os elementos",
    auth_errors: {
      invalid_email: "Formato de e-mail inválido.",
      user_disabled: "Esta conta de usuário foi desativada.",
      user_not_found: "E-mail ou senha incorretos.",
      wrong_password: "E-mail ou senha incorretos.",
      email_already_in_use: "Este e-mail já está em uso. Tente fazer login.",
      account_exists_with_different_credential: "Já existe uma conta com este e-mail usando um método de login diferente.",
      invalid_credential: "E-mail ou senha incorretos.",
      weak_password: "A senha é muito fraca (mínimo 6 caracteres).",
      operation_not_allowed: "Operação de login não permitida.",
      too_many_requests:
        "Muitas tentativas falhas. Tente novamente mais tarde.",
      default: "Ocorreu um erro inesperado. Tente novamente.",
    },
    email: "E-mail",
    confirm_email: "Confirmar E-mail",
    password: "Senha",
    email_mismatch: "Os e-mails não coincidem.",
    forgot_password_question: "Esqueceu sua senha?",
    no_account_link: "Não tem uma conta?",
    signup_now: "Cadastre-se",
    already_have_account_link: "Já tem uma conta?",
    login_now: "Entrar",
    check_email_title: "Verifique seu e-mail",
    recovery_sent_msg: "Enviamos um link de recuperação para:",
    back_to_login: "Voltar para o Login",
    send_link: "Enviar Link",

    // Character Modal
    char_modal_title: "Perfil do Personagem",
    dramatic_function: "Função Dramática",
    char_name_placeholder: "NOME DO PERSONAGEM",
    identity: "Identidade",
    identity_fullname_placeholder: "Ex: John Doe",
    identity_age_placeholder: "Ex: 35 anos",
    identity_profession_placeholder: "Ex: Detetive Particular",
    identity_social_class_placeholder: "Ex: Classe Média",
    identity_era_placeholder: "Ex: Londres, 1890",
    identity_location_placeholder: "Ex: Baker Street",
    psychology: "Psicologia",
    psychology_archetype_placeholder: "Ex: Herói Relutante",
    psychology_moral_placeholder: "Descreva os valores e princípios...",
    psychology_fears_placeholder: "O que o personagem teme...",
    psychology_goal_placeholder: "A maior motivação da história...",
    expression: "Expressão",
    expression_speech_placeholder: "Ex: Formal, rebuscado, sarcástico...",
    expression_humor_placeholder: "Ex: Humor ácido, irônico, inexistente...",
    expression_visual_placeholder: "Prompt detalhado para geração de imagem...",
    full_name: "Nome Completo",
    age: "Idade",
    profession: "Profissão/Função",
    social_class: "Classe Social",
    era_setting: "Época/Cenário",
    location: "Localização",
    real_person: "Pessoa Real?",
    archetype: "Arquétipo",
    personality_traits: "Traços de Personalidade",
    moral_code: "Código Moral / Tese",
    fears: "Medos / Dores",
    primary_goal: "Objetivo Principal",
    speech_style: "Estilo de Fala",
    topics: "Tópicos Frequentes",
    humor: "Senso de Humor",
    visual_desc: "Descrição Visual (Prompt do Avatar)",
    save_char: "Salvar Personagem",
    role_protagonist: "Protagonista",
    role_antagonist: "Antagonista",
    role_supporting: "Coadjuvante",
    role_extra: "Figurante",
    role_interviewee: "Entrevistado",
    role_narrator: "Narrador",
    role_mascot: "Mascote",
    import_characters: "Importar Personagens",
    import_characters_desc: "Extrair perfis do roteiro",
    char_modal_extract: "EXTRAÇÃO DE PERSONAGEM",
    analysis_complete_verify: "Análise Concluída - Verifique os dados",
    all_chars_have_profiles: "Todos os personagens detectados já possuem perfil.",
    ai_extraction_results: "Personagens Identificados",
    review_candidates: "Selecione os personagens que deseja importar.",
    deselect_all: "Desmarcar Todos",
    select_all: "Selecionar Todos",
    confirm_import: "Importar",
    character_profile_not_extracted: "Perfil ainda não extraído. Preencha manualmente ou use a Extração Automática na barra lateral.",
    char_extracting: "EXTRAINDO PERSONAGEM",
    auto_complete_profile: "✨ Auto-Completar Perfil",
    profile_auto_filled: "Perfil de {name} preenchido pela IA.",

    // Audit
    technical_error: "ERRO TÉCNICO",
    stylistic_suggestion: "SUG. ESTILÍSTICA",
    what_is_it: "O que é?",
    technical_error_tip: "Violações das regras Master Scenes que impedem o roteiro de ser filmável ou profissional (retira pontos)",
    stylistic_suggestion_tip: "Sugestões de subtexto e refinamento criativo (NÃO afeta a nota técnica)",
    selected: "selecionados",
    ignore: "Ignorar",
    applied: "Aplicado",
    ignored: "Ignorado",
    suggestions_found: "Sugestões de refinamento encontradas.",
    no_errors_found: "Nenhum erro técnico encontrado nesta rodada.",

    // Round / Status
    final_round: "Rodada Final",
    round_label: "Rodada",
    verified: "Verificado",

    // Character Diagnostic Paths
    path_a: "Caminho A: Ajustar Roteiro",
    path_b: "Caminho B: Atualizar Perfil",
    or: "Ou...",

    // Configurações
    mature_language: "Permitir Conteúdo Maduro",
    mature_language_desc: "Habilitar conteúdo sem filtros (palavrões, violência, temas adultos).",
    horror_mode: "Modo Terror & Violência",
    horror_mode_desc: "Habilitar tensão extrema, gore e descrições viscerais.",
  },
  es: {
    structure: "Estructura",
    expand: "Expandir",
    expand_modal: "Expandir Modal",
    collapse_modal: "Reducir Modal",
    condense: "Condensar",
    refine: "Refinar",
    chat: "Chat",
    info: "Info",
    translate: "Traducir",
    translating: "Traduciendo...",
    open_origin_chat: "Abrir chat original",
    chat_unavailable: "Chat de origen no disponible",
    number_scenes: "# Escenas",
    back: "Volver",
    import_title: "Importar Guion",
    import_error_title: "Error de Importación",
    methodology_error_title: "Error de Metodología",
    methodology_format_error: "El contenido proporcionado no cumple con el formato aceptado para una metodología.",
    import_error_msg: "El archivo que intenta importar no contiene un guion en el formato comúnmente aceptado.",
    import_desc: "Subir archivo .txt",
    paste_title: "Pegar Guion",
    paste_desc: "Editar texto sin formato",
    or_paste: "O pegar texto sin formato",
    paste_placeholder: "Pega aquí la sinopsis y/o la escaleta de tu historia",
    literal: "LITERAL",
    creative: "CREATIVO",
    cancel: "Cancelar",
    tag_placeholder: "Añade y presiona coma...",
    tag_error: "Los nombres propios o de personajes no están permitidos como etiquetas.",
    load_script: "Cargar Guion",
    start_script: "Iniciar Guion",
    ideation_title: "Nuevo Guion",
    ideation_desc: "Lluvia de ideas con IA",
    ideation_header: "Estudio de Ideación",
    generate_script: "Generar Guion",
    generate_new_script: "Generar Nueva Versión",
    generate_script_locked: "Discute la historia para desbloquear",
    script_ready_msg:
      "Tienes suficiente para crear un guion. Haz clic en el botón 'Generar Guion' o continúa chateando.",
    generating: "Generando...",
    importing: "Importando...",
    generating_animation: "Escribiendo Guion...",
    importing_script: "Importando Guión...",
    wait_message:
      "Por favor, espere. No cierre esta pantalla hasta que finalice.",
    input_placeholder: "Comienza tu historia presentando al menos un personaje y un conflicto",
    improve_prompt: "Mejorar Prompt",
    improving: "Mejorando...",

    // Script Versions
    script_version: "Versión",
    scene: "ESCENA",
    of: "DE",
    skip: "omitir",
    finishing: "finalizando...",
    rename: "Renombrar",
    delete: "Borrar",
    delete_script_confirm: "¿Borrar esta versión del guion?",
    delete_version_title: "Eliminar Versión de Guion",
    delete_version_msg: "¿Estás seguro? Puedes exportar antes de borrar.",
    export: "Exportar",
    export_pdf: "Exportar (.PDF)",
    trial_limit_scripts: "Límite alcanzado: 24 Guiones",
    trial_limit_reached:
      "Límite de la Versión de Prueba Alcanzado (Máx 6 Proyectos). Por favor elimine un proyecto antiguo para crear uno nuevo.",
    version_limit_reached:
      "Límite de Versiones Alcanzado. Por favor elimine versiones antiguas.",
    trial_limit_reached_title: "Límite de la Versión de Prueba Alcanzado (Máx 6 Proyectos)",
    trial_limit_reached_body: "Por favor elimine un proyecto antiguo para crear uno nuevo.",
    applying_corrections: "Aplicando correcciones...",
    applied_successfully: "¡Guion actualizado con éxito!",
    error_generate_version: "Error al generar nueva versión del guion.",
    update_script: "Generar Nueva Versión",
    updating_script: "Generando...",

    // Text Selection & Editing
    quote_in_chat: "Citar en Chat",
    edit_block: "Editar Párrafo",
    customize_character: "Personalizar Personaje",
    edit_dialogue: "Editar Diálogo",
    improve_dialogue: "Mejorar Diálogo",
    improve_dialogue_title: "Mejorar Diálogo",
    edit_modal_title: "Editar Bloque",
    rewrite_ai: "Reescribir con IA",
    rewriting: "Reescribiendo...",
    apply: "Aplicar",
    apply_corrections: "Aplicar Correcciones",
    apply_suggestion: "Aplicar Sugerencia",
    regenerate: "Re-analizar",
    original: "Original",

    pro_license: "Licencia Pro",
    tutorial_title: "Guía Rápida",
    tutorial_step_1_title: "Estudio de Ideación",
    tutorial_step_1_desc:
      "Empieza chateando con la IA. Discuta premisas y escenas. Cuando estés listo, haz clic en 'Generar Guion'.",
    tutorial_step_2_title: "Editor Inteligente",
    tutorial_step_2_desc:
      "Edita en la página. Usa [Enter] para cambiar elementos y [Tab] para alternar tipos (Acción > Personaje > Diálogo).",
    tutorial_step_3_title: "Puente de Producción",
    tutorial_step_3_desc:
      "Abre la pestaña 'Prompts' a la derecha para generar prompts de imagen y video basados en tu escena.",
    tutorial_step_4_title: "Análisis Estructural",
    tutorial_step_4_desc:
      "Usa la pestaña 'Config' para activar metodologías de McKee o Field y analizar tu ritmo.",
    close: "Cerrar",

    // Editor Help
    editor_help_title: "Cómo usar el editor",
    editor_help_intro: "Selecciona un párrafo para ver dos funciones:",
    editor_help_quote:
      "• Haz clic en 'Citar en Chat' para crear un nuevo guion inspirado en tu nueva entrada o sugerencias de IA",
    editor_help_edit:
      "• Haz clic en 'Editar Párrafo' para cambiarlo directamente. Esto no crea una nueva versión.",

    // Detailed Help Popup
    help_editor_body_title: "Cuerpo del Guion",
    help_editor_body_1: "Haz clic en Diálogo para mejorarlo.",
    help_editor_body_2: "Haz clic en Párrafo para editarlo.",
    help_editor_body_3: "Haz clic en Categorizar para añadir nuevos Elementos",
    help_toolbar_title: "Barra de Herramientas",
    help_toolbar_1: "añade/edita 27 Pilares - al editar genera nuevo guion",
    help_toolbar_2: "analiza el guion",
    help_sidebar_title: "Columna Derecha",
    help_sidebar_elements: "editar personajes, ubicaciones y atrezo",
    help_sidebar_actions_ai: "analiza guion y genera uno nuevo",
    help_sidebar_actions_kb: "aplica métodos de guion",
    help_sidebar_actions_analysis: "opciones de estructura de guion",
    help_sidebar_prompts_prod: "genera prompt para cada escena",
    help_sidebar_prompts_duration: "calcula tiempo de historia para video",

    // Sidebar Left
    projects: "Proyectos",
    new_project: "Nuevo Proyecto",
    rename_current: "Renombrar Actual",
    delete_current: "Eliminar Actual",
    scenes: "Escenas",
    no_scenes: "No hay escenas en el guion",
    pacing: "RITMO",
    delete_modal_title: "Eliminar Proyecto",
    delete_modal_body: "¿Seguro que quieres eliminar",
    delete_modal_warning: "Esta acción no se puede deshacer.",
    confirm: "Confirmar",
    untitled: "Sin Título",

    // Sidebar Right
    elements: "Elementos",
    actions: "Acciones",
    prompts: "Prompts",
    config: "CONFIGURACIÓN",

    // Sidebar Right (Config)
    knowledge_base: "Base de Conocimiento",
    synergy_mode: "Modo Sinergia Activo",
    method_mckee: "Método: R. McKee",
    method_mckee_desc: "Valores, Archplot, El Abismo.",
    method_field: "Método: Syd Field",
    method_field_desc: "Paradigma, 3 Actos, Pontos de Giro.",
    analysis_tools: "Directrices de Contenido",
    toast_limit_title: "Límite de Enfoque Alcanzado",
    toast_limit_message: "Si no quieres dar vida a un Frankenstein narrativo, hazte un favor y utiliza como máximo dos metodologías, o ve directo a la que realmente encaja con el guion que quieres desarrollar.",
    editor_settings: "Configuración del Editor",
    visual_atmosphere: "Atmosfera Visual",
    visual_atmosphere_desc: "Generar clima e iluminación.",
    character_arcs: "Arcos de Personaje",
    character_arcs_desc: "Rastrear progresión emocional.",
    plant_and_payoff: "Plant & Payoff",
    plant_and_payoff_desc: "Ingeniería de cohesión narrativa.",
    pan_with_space: "PAN con Barra de Espacio",
    pan_with_space_desc: "Mantenga Espacio y arrastre para mover la página.",
    line_pagination: "Paginación de Líneas",
    line_pagination_desc: "Mostrar números de línea en el editor.",
    custom_knowledge: "Conocimiento Personalizado",
    upload_methodology: "Subir tu metodología",
    custom_method_desc: "Metodología personalizada cargada",
    loading_methodology: "Cargando metodología...",
    drop_files: "Suelta los archivos aquí",

    // Sidebar Right (Tasks)
    ai_tasks: "Tareas IA",
    task_pacing: "Análisis de Ritmo",
    task_pacing_desc: "Identificar secciones lentas/rápidas.",
    task_camera: "Dirección de Cámaras",
    task_camera_desc:
      "Sugerir planos, ángulos, movimientos y enfoque por escena.",
    task_effects: "SFX/VFX Effects",
    task_effects_desc: "Sugerir soundFX y efectos.",
    task_effects_apply: "Generar Nueva Versión",
    task_camera_level: "Nivel de Complejidad",
    task_camera_essential: "Esencial",
    task_camera_pro: "Profesional",
    task_camera_auteur: "Autoral/Cinematográfico",
    task_camera_filter_toggle:
      "Aplicar solo en escenas sin direcciones de cámara",
    task_camera_intent: "Intención",
    task_camera_suggestions: "Sugerencias",
    task_camera_justification: "Justificación",
    task_camera_apply_btn: "Generar Nueva Versión",
    task_camera_ignore_scene: "Ignorar sugerencia de cámara",
    task_elements: "NUEVOS ELEMENTOS",
    task_elements_desc: "Sugerir personajes y objetos.",
    task_dialogue: "Mejorar Diálogo",
    task_dialogue_desc: "Refinar subtexto e naturalismo.",
    new_badge: "NUEVO",
    task_tone_analyzer: "Analizador de Tono",
    task_tone_analyzer_desc: "Analiza la consistencia de la voz y la alineación del género.",
    task_tone_analyzer_apply_prompt: "¿Desea aplicar estos ajustes tonales?",
    task_tone_analyzer_run: "Analizar Tono",
    task_script_review: "Revisión de Guion",
    task_script_review_desc: "Aplicar “Script Estándar” para corregir el formato.",
    task_script_review_run: "Corregir Guion",
    causality_chain: "Cadena de Causalidad",
    missing_trigger: "Gatillo no encontrado",
    plot_holes: "Agujeros de Guion",
    fix: "Corregir",
    strategic_suggestions: "Sugerencias Estratégicas",
    solve_plot_hole: "Resolver Agujero de Guion",
    choose_solution: "Elige una solución estratégica",
    action_beat: "Beat de Acción",
    insertion_point: "Punto de Inserción",
    ripple_effect: "Efecto Cascada",
    inject_in_scene: "Inyectar en la Escena",
    change_log: "Registro de Cambios",
    no_changes: "No hay correcciones necesarias. Guion limpio.",
    no_changes_title: "GUION IMPECABLE",
    tooltip_task_camera: "Lógica: Traducción de lo literario a lo visual. Fundamento: Transpone la intención dramática a planos técnicos (ángulos, movimientos y encuadres).",
    tooltip_task_elements: "Lógica: Ingeniería de profundidad. Fundamento: Escanea brechas en el ADN de la historia para sugerir personajes, objetos o locaciones que refuercen el tema.",
    tooltip_task_effects: "Lógica: Decodificación de atmósfera. Fundamento: Extrae del subtexto sugerencias de SFX y VFX que amplifican la experiencia sin inventar elementos fuera del texto.",
    tooltip_task_pacing: "Lógica: Script Doctor enfocado en la cadencia narrativa. Fundamento: Identifica el ritmo ideal para la emoción central del género, eliminando tiempos muertos o protegiendo silencios.",
    tooltip_task_tone_analyzer: "Lógica: Actúa como consultor de tono y voz. Fundamento: Garantiza la consistencia tonal, el alineamiento con el género y la diferenciación de las voces de los personajes.",
    tooltip_task_scriptaudit: "Lógica: Simula a un lector de guiones profesional. Fundamento: Se centra en el formato de 'Master Scenes', distinguiendo el estilo de los errores técnicos que dificultan el rodaje.",
    tooltip_task_transitions: "Lógica: Flujo de conciencia cinematográfico. Fundamento: Sugiere conexiones temáticas o visuales entre escenas para mantener la inmersión del espectador.",
    tooltip_task_plottwist: "Lógica: Recontextualización del pasado. Fundamento: Revelación que altera el impacto dramático y el significado de lo visto, respetando las leyes del género.",
    tooltip_task_plotaudit: "Lógica: Auditoría de Causalidade (A -> B). Fundamento: Verifica si los eventos ocurren por necesidad dramática o conveniencia, detectando fallos de lógica y motivación.",
    tooltip_task_continuity: "Lógica: Rigor lógico-temporal. Fundamento: Escanea el texto en busca de inconsistencias en objetos, tiempo y reglas del mundo entre escenas.",
    tooltip_task_chararcs: "Lógica: Viaje de la mentira a la verdad. Fundamento: Analiza la transformación emocional, comparando lo que el personaje quiere frente a lo que realmente necesita.",
    tooltip_task_visual_atmosphere: "Lógica: Concepto Visual. Fundamento: Traduce el subtexto dramático en luz, color y textura para crear una atmósfera inmersiva.",
    tooltip_task_plant_and_payoff: "Lógica: Ingeniería Narrativa. Fundamento: Audita la causalidad entre actos y escenas para asegurar que todas las semillas narrativas tengan una resolución significativa.",
    tooltip_task_script_review: "Lógica: Reconstrucción en formato Master Scene. Fundamento: Reescribe el guion para aplicar la formateación estándar, corrigiendo automáticamente problemas como Acción dentro del Diálogo.",
    tooltip_task_char_diagnostic: "Audita la consistencia entre los perfiles de los personajes y sus acciones en el guion.",
    task_transitions: "Transiciones",
    task_transitions_desc: "Sugerir cortes e fundidos.",
    task_plottwist: "Giro de Trama",
    task_plottwist_desc: "Recontextualización estructural.",
    task_plotaudit: "Auditoría de Trama",
    task_plotaudit_desc: "Detectar huecos en la trama.",
    task_scriptaudit: "Auditar Guion",
    task_scriptaudit_desc: "Evaluar y corregir el formato de Master Scenes.",
    task_scriptaudit_run: "Diagnosticar Guion",
    task_plotaudit_apply_prompt: "¿Desea generar un nuevo guion aplicando estas correcciones estructurales?",
    task_continuity: "Verificar Continuidad",
    task_continuity_desc: "Verificar consistencia de tiempo/objetos/reglas.",
    task_char_diagnostic: "Diagnóstico de Personajes",
    task_char_diagnostic_desc: "Audita la coherencia entre perfiles y guion.",
    task_char_diagnostic_btn: "Resolver Inconsistencia",
    char_diagnostic_running: "ANALIZANDO...",
    char_diagnostic_suggestions: "Sugerencias:",
    char_diagnostic_change_script: "Cambiar Guion",
    char_diagnostic_update_profile: "Actualizar Perfil",
    char_diagnostic_select_option: "Seleccione al menos una opción para aplicar.",
    char_diagnostic_discrepancy_prompt:
      "El guion indica [X], mientras que el Perfil indica [Y]. ¿Qué quieres?",
    msg_no_bible: "No se encontraron personajes en la base de datos (The Bible).",
    msg_no_continuity_issues: "No se detectaron inconsistencias.",
    inconsistency_detected: "¡INCONSISTENCIA DETECTADA!",
    inconsistency_instruction: "Haga clic en Diagnóstico de Personajes para cambiar el guion o actualizar el perfil.",
    task_chararcs: "Arcos de Personaje",
    task_chararcs_desc:
      "Analizar transformación emocional (Mentira vs Verdad).",
    task_visual_atmosphere: "Atmósfera Visual",
    task_visual_atmosphere_desc: "Reescribir escenas con clima e iluminación.",
    task_plant_and_payoff: "Plant & Payoff",
    task_plant_and_payoff_desc: "Ingeniería de cohesión narrativa.",
    literary_scripts: "PARA APLICAR EN GUIONES LITERARIOS",
    technical_scripts: "PARA CREAR GUIONES TÉCNICOS",
    analyzing: "Analizando...",
    enable_tasks_msg: "Genera un guion para habilitar tareas",

    // Sidebar Right (Prompts)
    production_bridge: "Asistente de Prompts",
    select_scene: "SELECIONAR ESCENA",
    select_shot: "SELECCIONAR PLANO",
    select_shot_placeholder: "Seleccionar plano...",
    gen_visual_prompts: "Generar Prompts Visuales",
    gen_shot_list: "Generando Prompts",
    no_shots_generated: "No se generaron planos.",
    import_to_enable: "Importa un guion para habilitar",
    ai_director: "Director IA en Espera",
    ai_director_desc:
      "Seleccione una escena y un shot/plano (opcional). Luego haga clic en Generar... para crear prompts de imágenes y videos para toda la escena o para el shot seleccionado.",
    export_prompts: "Exportar Prompts",
    edit_prompt: "Editar",
    save_prompt: "Guardar",
    prompt_results_title: "Resultados de la generación de prompts",
    image_prompts: "PROMPTS DE IMAGEN",
    video_prompts: "PROMPTS DE VIDEO",
    shot_label: "PLANO",
    ai_engine_status: "MOTOR DE PRODUCCIÓN IA • STANDBY",
    footer_tag: "SISTEMA OPERATIVO AUTEUR — v3.1 PROMPT ENGINE",
    visual_action_processing: "Descripción visual en proceso...",
    select_scene_to_reveal: "Selecciona una escena para mostrar los prompts",
    cinematic_shot: "PLANO CINEMATOGRÁFICO",
    view_results: "VER RESULTADOS",
    prompts_generated: "Prompts Generados",
    ai_director_standby: "DIRECTOR IA EN ESPERA",
    select_scene_shot_warning: "SELECCIONE ESCENA/SHOT",

    // Duration Estimation
    duration_estimate: "Estimación de Duración",
    estimating: "Estimando...",
    duration_total: "Duración Total",
    probable_range: "Rango Probable",
    pacing_slider: "Ajuste de Ritmo",
    slow: "Lento",
    fast: "Rápido",
    normal: "Normal",
    scene_breakdown: "Desglose de Escenas",
    classification: "Formato",
    reasoning: "Justificación",
    minutes: "min",
    estimated_total_duration: "Tiempo Total Estimado",
    fast_pacing: "Ritmo Rápido",
    slow_pacing: "Ritmo Lento",
    ai_analysis: "Análisis de IA",
    scene_slug: "ESCENA / SLUG",
    time: "TIEMPO",

    // Sidebar Right (Elements)
    characters: "Personajes",
    no_characters: "No se detectaron personajes.",
    no_description: "Sin descripción disponible.",
    locations: "Ubicaciones",
    no_locations: "No se detectaron ubicaciones.",
    props: "Atrezo",
    no_props: "No se detectaron objetos.",
    lines: "Líneas",
    char_name: "Nombre del Personaje",
    prop_name: "Nombre del Objeto",
    loc_name: "Nombre de la Locación",
    char_desc: "Descripción / Perfil",
    save: "Guardar",
    apply_to_script: "Aplicar al guion",
    improve_with_ai: "Mejorar con IA",
    no_sfx: "No se detectarón SFX/VFX.",
    no_camera: "No se detectarón direcciones de cámara.",
    no_transitions: "No se detectarón transiciones.",
    go_to_scene: "Ir a la escena:",

    // Story Pillars
    pillars_title: "Pilares de la Historia",
    pillars: {
      protagonist: {
        name: "Protagonista",
        description: "Personaje central que conduce la narrativa.",
      },
      objective: {
        name: "Objetivo",
        description: "Meta clara y mensurable del protagonista.",
      },
      antagonist: {
        name: "Antagonista / Fuerza",
        description: "Fuerza que impide el progreso del protagonista.",
      },
      conflict: {
        name: "Conflicto",
        description: "Tensión principal de la historia.",
      },
      stakes: {
        name: "Stakes (En Juego)",
        description: "Lo que está en juego si el protagonista falla.",
      },
      setting: {
        name: "Escenario / Era",
        description: "Dónde y cuándo ocurre la historia.",
      },
      theme: {
        name: "Tema",
        description: "Idea central o argumento de la obra.",
      },
      internal_desire: {
        name: "Deseo Interno",
        description: "Necesidad emocional del protagonista.",
      },
      tragic_flaw: {
        name: "Falla Trágica",
        description: "Defecto que genera conflicto.",
      },
      arc: {
        name: "Arco de Personaje",
        description: "Transformación a lo largo de la historia.",
      },
      inciting_incident: {
        name: "Incidente Incitante",
        description: "Evento que inicia el viaje.",
      },
      plan: {
        name: "Plan del Protagonista",
        description: "Estrategia para lograr el objetivo.",
      },
      obstacles: {
        name: "Obstáculos Progresivos",
        description: "Dificultades crecientes.",
      },
      no_return: {
        name: "Punto de No Retorno",
        description: "Decisión irreversible.",
      },
      climax: { name: "Clímax", description: "Confrontación máxima." },
      resolution: {
        name: "Resolución",
        description: "Nuevo equilibrio final.",
      },
      motif: {
        name: "Motivo Recorrente",
        description: "Elemento simbólico repetido.",
      },
      irony: {
        name: "Ironía Dramática",
        description: "Contraste entre intención y resultado.",
      },
      moral_cost: {
        name: "Regla Moral / Costo Ético",
        description: "Precio de ganar.",
      },
      genre_promise: {
        name: "Promesa de Género",
        description: "Expectativa creada en el público.",
      },
      tone: { name: "Tono", description: "Actitud emocional de la narrativa." },
      pacing: {
        name: "Ritmo / Escalada",
        description: "Progresión de la tensión.",
      },
      trigger: {
        name: "Gatillo Emocional",
        description: "Emoción principal provocada en el público.",
      },
      plant_payoff: {
        name: "Plant & Payoff",
        description: "Preparación y recompensa narrativa.",
      },
      pov: {
        name: "Punto de Vista Narrativo",
        description: "Quién conduce emocionalmente.",
      },
      world_conflict: {
        name: "Mundo en Conflicto",
        description: "Cómo reacciona el mundo.",
      },
      ending_feeling: {
        name: "Final Emocional",
        description: "Sensación dejada en el público.",
      },
    },
    pillar_protagonist_placeholder: "¿Quién es el héroe? (Nombre, Edad, Rol)",
    pillar_goal_placeholder: "¿Qué quiere? (Objetivo tangible)",
    pillar_antagonist_placeholder:
      "¿Quién/qué impide al protagonista? [persona, sistema, naturaleza, sí mismo]",
    pillar_conflict_placeholder: "Dinámica del conflicto central.",
    pillar_stakes_placeholder:
      "¿Qué pierde si falla? Consecuencias externas e internas.",
    pillar_setting_placeholder: "¿Dónde y Cuándo? (Reglas del mundo)",
    apply_pillars: "Aplicar Pilares",
    auto_suggest: "Auto-Sugerir",
    define_pillars: "Definir Pilares",
    analyzing_pillars: "Analizando ADN del Guion...",
    detected_format: "Formato Detectado",
    format_film: "Cine",
    format_animation: "Animación",
    format_documentary: "Documental",
    format_advertisement: "Comercial",
    add_pillars: "Añadir Pilares",
    add_to_chat_btn: "Añadir al Chat",
    regenerate_all: "Regenerar Todo",

    // Project Creation Modal
    start_script_modal_title: "Empezar Nuevo Guion",
    edit_title_page: "Editar Página de Título",
    project_title_label: "Título Provisional",
    author_label: "Nombre del Autor",
    based_on_label: "Basado en, Si hay",
    contact_info_label: "E-mail / Teléfono",
    written_by_label: "Escrito por",
    create_project_btn: "Crear Proyecto",
    recategorize: "Recategorizar",
    move_to_existing: "Mover a categoría existente",
    create_new_category: "Crear nueva categoría",
    new_category_name: "Nombre de la nueva categoría",
    category: "Categoría",
    ignore_element: "Ignorar Elemento",
    elements_footer_msg: "Genera un guión para habilitar los elementos",
    auth_errors: {
      invalid_email: "Formato de correo inválido.",
      user_disabled: "Esta cuenta de usuario ha sido deshabilitada.",
      user_not_found: "Correo o contraseña incorrectos.",
      wrong_password: "Correo o contraseña incorrectos.",
      email_already_in_use: "Este correo ya está en uso. Intenta iniciar sesión.",
      account_exists_with_different_credential: "Ya existe una cuenta con este correo usando un método de inicio de sesión diferente.",
      invalid_credential: "Correo o contraseña incorrectos.",
      weak_password: "La contraseña es muy débil (mín. 6 caracteres).",
      operation_not_allowed: "Operación de acceso no permitida.",
      too_many_requests: "Demasiados intentos fallidos. Inténtalo más tarde.",
      default: "Ocurrió un error inesperado. Inténtalo de nuevo.",
    },
    email: "Correo electrónico",
    confirm_email: "Confirmar correo electrónico",
    password: "Contraseña",
    email_mismatch: "Los correos electrónicos no coinciden.",
    forgot_password_question: "¿Olvidaste tu contraseña?",
    no_account_link: "¿No tienes una cuenta?",
    signup_now: "Regístrate",
    already_have_account_link: "¿Ya tienes una cuenta?",
    login_now: "Inicia sesión",
    check_email_title: "Revisa tu correo",
    recovery_sent_msg: "Enviamos un enlace de recuperación a:",
    back_to_login: "Volver al inicio de sesión",
    send_link: "Enviar enlace",

    // Character Modal
    char_modal_title: "Perfil del Personaje",
    dramatic_function: "Función Dramática",
    char_name_placeholder: "NOMBRE DEL PERSONAJE",
    identity: "Identidad",
    identity_fullname_placeholder: "Ej: Juan Pérez",
    identity_age_placeholder: "Ej: 35 años",
    identity_profession_placeholder: "Ej: Detective Privado",
    identity_social_class_placeholder: "Ej: Clase Media",
    identity_era_placeholder: "Ej: Londres, 1890",
    identity_location_placeholder: "Ej: Calle Baker",
    psychology: "Psicología",
    psychology_archetype_placeholder: "Ej: Héroe Reluctante",
    psychology_moral_placeholder: "Describe los valores y principios...",
    psychology_fears_placeholder: "Lo que el personaje teme...",
    psychology_goal_placeholder: "La mayor motivación de la historia...",
    expression: "Expresión",
    expression_speech_placeholder: "Ej: Formal, rebuscado, sarcástico...",
    expression_humor_placeholder: "Ej: Humor ácido, irónico, inexistente...",
    expression_visual_placeholder: "Prompt detallado para la generación de imágenes...",
    full_name: "Nombre Completo",
    age: "Edad",
    profession: "Profesión/Función",
    social_class: "Clase Social",
    era_setting: "Época/Escenario",
    location: "Ubicación",
    real_person: "¿Persona Real?",
    archetype: "Arquetipo",
    personality_traits: "Rasgos de Personalidad",
    moral_code: "Código Moral / Tesis",
    fears: "Miedos / Dolores",
    primary_goal: "Objetivo Principal",
    speech_style: "Estilo de Habla",
    topics: "Temas Frecuentes",
    humor: "Sentido del Humor",
    visual_desc: "Descripción Visual (Prompt del Avatar)",
    save_char: "Guardar Personaje",
    role_protagonist: "Protagonista",
    role_antagonist: "Antagonista",
    role_supporting: "Coadyuvante",
    role_extra: "Figurante",
    role_interviewee: "Entrevistado",
    role_narrator: "Narrador",
    role_mascot: "Mascota",
    import_characters: "Importar Personajes",
    import_characters_desc: "Extraer perfiles del guion",
    char_modal_extract: "EXTRACCIÓN DE PERSONAJE",
    analysis_complete_verify: "Análisis Completo - Verifique los datos",
    all_chars_have_profiles: "Todos los personajes detectados ya tienen perfiles.",
    ai_extraction_results: "Personajes Identificados",
    review_candidates: "Seleccione los personajes que desea importar.",
    deselect_all: "Deseleccionar Todo",
    select_all: "Seleccionar Todo",
    confirm_import: "Importar",
    character_profile_not_extracted: "Perfil aún no extraído. Rellene manualmente o utilice la Extracción Automática en la barra lateral.",
    char_extracting: "EXTRAYENDO PERSONAJE",
    auto_complete_profile: "✨ Auto-Completar Perfil",
    profile_auto_filled: "Perfil de {name} completado por la IA.",

    // Audit
    technical_error: "ERROR TÉCNICO",
    stylistic_suggestion: "SUG. ESTILÍSTICA",
    what_is_it: "¿Qué es?",
    technical_error_tip: "Violaciones de las reglas Master Scenes que impiden que el guion sea filmable o profesional (resta puntos)",
    stylistic_suggestion_tip: "Sugerencias de subtexto y refinamiento creativo (NO afecta la nota técnica)",
    selected: "seleccionados",
    ignore: "Ignorar",
    applied: "Aplicado",
    ignored: "Ignorado",

    // Round / Status
    final_round: "Ronda Final",
    round_label: "Ronda",
    verified: "Verificado",

    // Character Diagnostic Paths
    path_a: "Camino A: Ajustar Guion",
    path_b: "Camino B: Actualizar Perfil",
    or: "O...",

    // Configuración
    mature_language: "Permitir Contenido Maduro",
    mature_language_desc: "Habilitar contenido sin filtros (palabras fuertes, violencia de adultos).",
    horror_mode: "Modo Terror & Violencia",
    horror_mode_desc: "Habilitar tensión extrema, gore y descripciones viscerales.",
  },
  fr: {
    structure: "Structure",
    expand: "Étendre",
    expand_modal: "Agrandir le Modal",
    collapse_modal: "Réduire le Modal",
    condense: "Condenser",
    refine: "Affiner",
    chat: "Chat",
    info: "Info",
    translate: "Traduire",
    translating: "Traduction...",
    open_origin_chat: "Ouvrir le chat original",
    chat_unavailable: "Chat d'origine indisponible",
    number_scenes: "# Scènes",
    back: "Retour",
    import_title: "Importer un Scénario",
    import_error_title: "Erreur d'importation",
    methodology_error_title: "Erreur de Méthodologie",
    methodology_format_error: "Le contenu fourni n’est pas conforme au format accepté pour une méthodologie.",
    import_error_msg: "Le fichier que vous essayez d'importer ne contient pas de script conforme au format couramment accepté.",
    import_desc: "Télécharger fichier .txt",
    paste_title: "Coller Scénario",
    paste_desc: "Éditer le texte brut",
    or_paste: "Ou coller du texte brut",
    paste_placeholder: "Collez ici le synopsis et/ou le séquencier de votre histoire",
    literal: "LITÉRAL",
    creative: "CRÉATIF",
    cancel: "Annuler",
    tag_placeholder: "Ajoutez et appuyez sur la virgule...",
    tag_error: "Les noms propres ou de personnages ne sont pas autorisés en tant que tags.",
    load_script: "Charger Scénario",
    start_script: "Démarrer Scénario",
    ideation_title: "Nouveau Scénario",
    ideation_desc: "Brainstorming avec l'IA",
    ideation_header: "Studio d'Idéation",
    generate_script: "Générer Scénario",
    generate_new_script: "Générer Nouvelle Version",
    generate_script_locked: "Discutez de l'histoire pour débloquer",
    script_ready_msg:
      "Vous en avez assez pour créer un script. Cliquez sur le bouton 'Générer Scénario' ou continuez à discuter.",
    generating: "Génération...",
    importing: "Importation...",
    generating_animation: "Écriture du Scénario...",
    importing_script: "Importation du Scénario...",
    wait_message: "Veuillez patienter. Ne fermez pas cet écran avant la fin.",
    input_placeholder: "Commencez votre histoire en présentant au moins un personnage et un conflit",
    improve_prompt: "Améliorer Prompt",
    improving: "Amélioration...",

    // Script Versions
    script_version: "Version",
    scene: "SCÈNE",
    of: "DE",
    skip: "passer",
    finishing: "finalisation...",
    rename: "Renommer",
    delete: "Supprimer",
    delete_script_confirm: "Supprimer cette version?",
    delete_version_title: "Supprimer la version",
    delete_version_msg:
      "Êtes-vous sûr ? Vous pouvez exporter avant de supprimer.",
    export: "Exporter",
    export_pdf: "Exporter (.PDF)",
    trial_limit_scripts: "Limite atteint: 24 Scénarios",
    trial_limit_reached:
      "Limite de la Version d'Essai Atteinte (Max 6 Projets). Veuillez supprimer un ancien projet pour en créer un nouveau.",
    version_limit_reached:
      "Limite de Versions Atteinte. Veuillez supprimer les anciennes versions.",
    applying_corrections: "Application des corrections...",
    applied_successfully: "Scénario mis à jour avec succès!",
    trial_limit_reached_title: "Limite de la Version d'Essai Atteinte (Max 6 Projets)",
    trial_limit_reached_body: "Veuillez supprimer un ancien projet pour en créer un nouveau.",
    error_generate_version:
      "Échec de la génération de la nouvelle version du scénario.",
    update_script: "Générer Nouvelle Version",
    updating_script: "Génération...",

    // Text Selection & Editing
    quote_in_chat: "Citar dans le Chat",
    edit_block: "Modifier Paragraphe",
    customize_character: "Personnaliser le Personnage",
    edit_dialogue: "Modifier Dialogue",
    improve_dialogue: "Améliorer Dialogue",
    improve_dialogue_title: "Améliorer Dialogue",
    edit_modal_title: "Modifier Bloc",
    rewrite_ai: "Réécrire avec IA",
    rewriting: "Réécriture...",
    apply: "Appliquer",
    apply_corrections: "Appliquer les Corrections",
    apply_suggestion: "Appliquer la Suggestion",
    regenerate: "Re-analyser",
    original: "Original",

    pro_license: "Licence Pro",
    tutorial_title: "Guide Rapide",
    tutorial_step_1_title: "Studio d'Idéation",
    tutorial_step_1_desc:
      "Commencez par discuter avec l'IA. Parlez de votre prémisse. Cliquez sur 'Générer Scénario' pour créer le brouillon.",
    tutorial_step_2_title: "Éditeur Intelligent",
    tutorial_step_2_desc:
      "Éditez sur la page. Utilisez [Entrée] pour changer d'élément et [Tab] pour alterner les types (Action > Personnage).",
    tutorial_step_3_title: "Pont de Production",
    tutorial_step_3_desc:
      "Ouvrez l'onglet 'Prompts' à droite pour générer des prompts image et vidéo basés sur votre scène.",
    tutorial_step_4_title: "Analyse Structurelle",
    tutorial_step_4_desc:
      "Utilisez 'Config' pour activer les méthodes McKee ou Field et analyser le rythme.",
    close: "Fermer",

    // Editor Help
    editor_help_title: "Comment utiliser l'éditeur",
    editor_help_intro:
      "Sélectionnez un paragraphe pour révéler deux fonctions:",
    editor_help_quote:
      "• Cliquez sur 'Citar dans le Chat' pour créer une nouvelle version du script inspirée par votre saisie ou l'IA",
    editor_help_edit:
      "• Cliquez sur 'Modifier Paragraphe' pour le changer directement. Cela ne crée pas de nouvelle version.",

    // Detailed Help Popup
    help_editor_body_title: "Corps du Scénario",
    help_editor_body_1: "Cliquez sur Dialogue pour l'améliorer.",
    help_editor_body_2: "Cliquez sur Paragraphe pour le modifier.",
    help_toolbar_title: "Barre d'outils",
    help_toolbar_1: "génère un nouveau scénario si modifié",
    help_toolbar_2: "analyse le scénario",
    help_sidebar_title: "Colonne de Droite",
    help_sidebar_elements: "modifier personnages, lieux et accessoires",
    help_sidebar_actions_ai: "analyse le scénario et en génère un nouveau",
    help_sidebar_actions_kb: "applique des méthodes de scénario",
    help_sidebar_actions_analysis: "options de structure de scénario",
    help_sidebar_prompts_prod: "génère um prompt pour cada scène",
    help_sidebar_prompts_duration:
      "calcule le temps de l'histoire pour la vidéo",

    // Sidebar Left
    projects: "Projets",
    new_project: "Nouveau Projet",
    rename_current: "Renommer Actuel",
    delete_current: "Supprimer Actuel",
    scenes: "Scènes",
    no_scenes: "Aucune scène dans le scénario",
    pacing: "RYTHME",
    delete_modal_title: "Supprimer le Projet",
    delete_modal_body: "Êtes-vous sûr de vouloir supprimer",
    delete_modal_warning: "Cette action est irréversible.",
    confirm: "Confirmer",
    untitled: "Sans Titre",

    // Sidebar Right
    elements: "Éléments",
    actions: "Actions",
    prompts: "Prompts",
    config: "SETTINGS",

    // Sidebar Right (Config)
    knowledge_base: "Base de Connaissances",
    synergy_mode: "Mode Synergie Actif",
    method_mckee: "Méthode : R. McKee",
    method_mckee_desc: "Valeurs, Archplot, Le Gouffre.",
    method_field: "Méthode : Syd Field",
    method_field_desc: "Paradigme, 3 Actes, Points d'Intrigue.",
    analysis_tools: "Directives de Contenu",
    toast_limit_title: "Limite de Concentration Atteinte",
    toast_limit_message: "Si vous ne voulez pas voir naître un Frankenstein narratif, faites-vous plaisir et limitez-vous à deux méthodologies, ou choisissez simplement celle qui colle vraiment au scénario que vous voulez créer.",
    editor_settings: "Paramètres de l'éditeur",
    visual_atmosphere: "Atmosphère Visuelle",
    visual_atmosphere_desc: "Générer l'ambiance et l'éclairage.",
    character_arcs: "Arcs de Personnage",
    character_arcs_desc: "Suivre la progression émotionnelle.",
    plant_and_payoff: "Plant & Payoff",
    plant_and_payoff_desc: "Ingénierie de la cohésion narrative.",
    pan_with_space: "PAN avec Barre d'Espace",
    pan_with_space_desc:
      "Maintenez Espace et faites glisser pour déplacer la page.",
    line_pagination: "Pagination des Lignes",
    line_pagination_desc: "Afficher les numéros de ligne dans l'éditeur.",
    custom_knowledge: "Connaissances Personnalisées",
    upload_methodology: "Télécharger votre méthodologie",
    custom_method_desc: "Méthodologie personnalisée chargée",
    loading_methodology: "Chargement de la méthodologie...",
    drop_files: "Déposez les fichiers ici",

    // Sidebar Right (Tasks)
    ai_tasks: "Tâches IA",
    task_pacing: "Analyse du Rythme",
    task_pacing_desc: "Identifier les sections lentes/rapides.",
    task_camera: "Direction de Caméra",
    task_camera_desc: "Suggérer plans, angles, mouvements et focus par scène.",
    task_effects: "SFX/VFX Effects",
    task_effects_desc: "Suggérer des effets sonores et visuels.",
    task_effects_apply: "Générer une Nouvelle Version",
    task_camera_level: "Niveau de Complexité",
    task_camera_essential: "Essentiel",
    task_camera_pro: "Professionnel",
    task_camera_auteur: "Auteur/Cinématique",
    task_camera_filter_toggle:
      "Appliquer uniquement aux scènes sans directions de caméra",
    task_camera_intent: "Intention",
    task_camera_suggestions: "Suggérer",
    task_camera_justification: "Justification",
    task_camera_apply_btn: "Générer une Nouvelle Version",
    task_camera_ignore_scene: "Ignorer la suggestion de caméra",
    task_elements: "NOUVEAUX ÉLÉMENTS",
    task_elements_desc: "Suggérer personnages et objets.",
    task_dialogue: "Améliorer Dialogue",
    task_dialogue_desc: "Affiner sous-texte et naturalisme.",
    new_badge: "NOUVEAU",
    task_tone_analyzer: "Analyseur de Ton",
    task_tone_analyzer_desc: "Analyse la cohérence de la voix et l'alignement des genres.",
    task_tone_analyzer_apply_prompt: "Souhaitez-vous appliquer ces ajustements tonals ?",
    task_tone_analyzer_run: "Analyser le Ton",
    task_script_review: "Révision de Scénario",
    task_script_review_desc: "Appliquer le « Script Standard » pour corriger la mise en forme.",
    task_script_review_run: "Corriger Scénario",
    causality_chain: "Chaîne de Causalité",
    missing_trigger: "Déclencheur manquant",
    plot_holes: "Trous de Scénario",
    fix: "Corriger",
    strategic_suggestions: "Suggestions Stratégiques",
    solve_plot_hole: "Résoudre le Trou de Scénario",
    choose_solution: "Choisissez une solution stratégique",
    action_beat: "Beat d'Action",
    insertion_point: "Point d'Insertion",
    ripple_effect: "Effet de Ricochet",
    inject_in_scene: "Injecter dans la Scène",
    change_log: "Journal des modifications",
    no_changes: "Aucune correction nécessaire. Scénario propre.",
    no_changes_title: "SCÉNARIO IMPECCABLE",
    tooltip_task_camera: "Logique : Traduction du littéraire au visuel. Fondement : Transpose l'intention dramatique en plans techniques (angles, mouvements et cadrages).",
    tooltip_task_elements: "Logique : Ingénierie de profondeur. Fondement : Analyse les lacunes dans l'ADN de l'histoire pour suggérer des personnages, des accessoires ou des lieux qui renforcent le thème.",
    tooltip_task_effects: "Logique : Décodage d'atmosphère. Fondement : Extrait du sous-texte des suggestions de SFX et VFX qui amplifient l'expérience sans inventer d'éléments hors texte.",
    tooltip_task_pacing: "Logique : Script Doctor axé sur la cadence narrative. Fondement : Identifie le rythme idéal pour l'émotion centrale du genre, en éliminant les temps morts ou en protégeant les silences.",
    tooltip_task_tone_analyzer: "Logique : Agit en tant que consultant en ton et en voix. Fondement : Assure la cohérence tonale, l'alignement avec le genre et la différenciation des voix des personnages.",
    tooltip_task_scriptaudit: "Logique : Simule un lecteur de script professionnel. Fondement : Se concentre sur le formatage 'Master Scenes', en distinguant le style des erreurs techniques qui entravent le tournage.",
    tooltip_task_transitions: "Logique : Flux de conscience cinématographique. Fondement : Suggère des connexions thématiques ou visuelles entre les scènes pour maintenir l'immersion du spectateur.",
    tooltip_task_plottwist: "Logique : Recontextualisation du passé. Fondement : Révélation qui modifie l'impact dramatique et le sens de ce qui a été vu, en respectant les lois du genre.",
    tooltip_task_plotaudit: "Logique : Audit de Causalité (A -> B). Fondement : Vérifie si les événements se produisent par nécessité dramatique ou par commodité, en détectant les failles de logique et de motivation.",
    tooltip_task_continuity: "Logique : Rigueur logico-temporelle. Fondement : Analyse le texte à la recherche d'incohérences concernant les objets, le temps et les règles du monde entre les scènes.",
    tooltip_task_chararcs: "Logique : Voyage du mensonge à la vérité. Fondement : Analyse la transformation émotionnelle, en comparant ce que le personnage veut par rapport à ce dont il a réellement besoin.",
    tooltip_task_visual_atmosphere: "Logique : Concept Visuel. Fondement : Traduit le sous-texte dramatique en lumière, couleur et texture pour créer une atmosphère immersive.",
    tooltip_task_plant_and_payoff: "Logique : Ingénierie Narrative. Fondement : Audite la causalité entre les actes et les scènes pour garantir que todas les graines narratives ont une issue significative.",
    tooltip_task_script_review: "Logique : Reconstruction au format Master Scene. Fondement : Réécrit le scénario afin d’appliquer la mise en forme standard, en corrigeant automatiquement des problèmes tels que l’Action à l’intérieur du Dialogue.",
    tooltip_task_char_diagnostic: "Audite la cohérence entre les profils des personnages et leurs actions dans le scénario.",
    task_transitions: "Transitions",
    task_transitions_desc: "Suggérer coupes et fondus.",
    task_plottwist: "Coup de Théâtre",
    task_plottwist_desc: "Recontextualisation structurelle.",
    task_plotaudit: "Audit de l'Intrigue",
    task_plotaudit_desc: "Détecter les incohérences.",
    task_scriptaudit: "Auditer le Scénario",
    task_scriptaudit_desc: "Évaluer et corriger le formatage Master Scenes.",
    task_scriptaudit_run: "Diagnostiquer le Scénario",
    task_plotaudit_apply_prompt: "Voulez-vous générer un nouveau scénario en appliquant ces corrections structurelles ?",
    task_continuity: "Vérifier la Continuité",
    task_continuity_desc: "Vérifier la cohérence du temps/objets/règles.",
    task_char_diagnostic: "Diagnostic des Personnages",
    task_char_diagnostic_desc: "Audit de la cohérence entre profils et script.",
    task_char_diagnostic_btn: "Résoudre l'Incohérence",
    char_diagnostic_running: "ANALYSE...",
    char_diagnostic_suggestions: "Suggestions :",
    char_diagnostic_change_script: "Modifier Scénario",
    char_diagnostic_update_profile: "Mettre à jour le Profil",
    char_diagnostic_select_option:
      "Veuillez sélectionner au moins une option à appliquer.",
    char_diagnostic_discrepancy_prompt:
      "Le scénario indique [X], alors que le Profil indique [Y]. Que voulez-vous ?",
    msg_no_bible: "Aucun personnage trouvé dans la base de données (The Bible).",
    msg_no_continuity_issues: "Aucune incohérence détectée.",
    inconsistency_detected: "INCOHÉRENCE DÉTECTÉE !",
    inconsistency_instruction: "Cliquez sur Diagnostic des Personnages pour modifier le scénario ou mettre à jour le profil.",
    task_chararcs: "Arcs de Personnage",
    task_chararcs_desc:
      "Analyser la transformation émotionnelle (Mensonge vs Vérité).",
    task_visual_atmosphere: "Atmosphère Visuelle",
    task_visual_atmosphere_desc: "Réécrire les scènes avec ambiance et lumière.",
    task_plant_and_payoff: "Plant & Payoff",
    task_plant_and_payoff_desc: "Ingénierie de la cohésion narrative.",
    literary_scripts: "POUR APPLIQUER SUR SCÉNARIOS LITTÉRAIRES",
    technical_scripts: "POUR CRÉER DES SCÉNARIOS TECHNIQUES",
    analyzing: "Analyse...",
    enable_tasks_msg: "Générez un scénario pour activer les tâches",

    // Sidebar Right (Prompts)
    production_bridge: "Assistant de Prompts",
    select_scene: "SÉLECTIONNER UNE SCÈNE",
    select_shot: "SÉLECTIONNER PLAN/PRÉLÈVEMENT",
    select_shot_placeholder: "Sélectionner un plan/prélèvement...",
    gen_visual_prompts: "Générer Prompts Visuels",
    gen_shot_list: "Génération des Prompts",
    no_shots_generated: "Aucun plan généré.",
    import_to_enable: "Importer un scénario pour activer",
    ai_director: "Directeur IA en Veille",
    ai_director_desc:
      "Sélectionnez une scène et un shot/plan (facultatif). Cliquez ensuite sur Générer... pour créer des prompts d'images et de vidéos pour toute la scène ou pour le shot sélectionné.",
    export_prompts: "Exporter Prompts",
    edit_prompt: "Modifier",
    save_prompt: "Enregistrer",
    prompt_results_title: "Résultats de la génération de prompts",
    image_prompts: "PROMPTS D'IMAGE",
    video_prompts: "PROMPTS DE VIDÉO",
    shot_label: "PLAN",
    ai_engine_status: "MOTEUR DE PRODUCTION IA • STANDBY",
    footer_tag: "SYSTÈME OPÉRATIONNEL AUTEUR — v3.1 PROMPT ENGINE",
    visual_action_processing: "Description visuelle en cours...",
    select_scene_to_reveal: "Sélectionnez une scène pour afficher les prompts",
    cinematic_shot: "PLAN CINÉMATOGRAPHIQUE",
    view_results: "VOIR LES RÉSULTATS",
    prompts_generated: "Prompts Générés",
    ai_director_standby: "DIRECTEUR IA EN VEILLE",
    select_scene_shot_warning: "SÉLECTIONNEZ SCÈNE/PLAN",

    // Duration Estimation
    duration_estimate: "Estimation de la Durée",
    estimating: "Estimation...",
    duration_total: "Durée Totale",
    probable_range: "Plage Probable",
    pacing_slider: "Ajustement du Rythme",
    slow: "Lent",
    fast: "Rapide",
    normal: "Normal",
    scene_breakdown: "Détail par Scène",
    classification: "Format",
    reasoning: "Justification",
    minutes: "min",
    estimated_total_duration: "Durée Totale Estimée",
    fast_pacing: "Rythme Rapide",
    slow_pacing: "Rythme Lent",
    ai_analysis: "Analyse de l'IA",
    scene_slug: "SCÈNE / SLUG",
    time: "TEMPS",

    // Sidebar Right (Elements)
    characters: "Personnage",
    no_characters: "Aucun personnage détecté.",
    no_description: "Aucune description disponible.",

    locations: "Lieux",
    no_locations: "Aucun lieu détecté.",
    props: "Accessoires",
    no_props: "Aucun accessoire détecté.",
    lines: "Répliques",
    char_name: "Nom du Personnage",
    prop_name: "Nom de l'Accessoire",
    loc_name: "Nom du Lieu",
    char_desc: "Description / Profil",
    save: "Sauver",
    apply_to_script: "Appliquer au scénario",
    improve_with_ai: "Améliorer avec l'IA",
    no_sfx: "Aucun SFX/VFX détecté.",
    no_camera: "Aucune direction de caméra détectée.",
    no_transitions: "Aucune transition détectée.",
    go_to_scene: "Aller à la scène :",

    // Story Pillars
    pillars_title: "Piliers de l'Histoire",
    pillars: {
      protagonist: {
        name: "Protagoniste",
        description: "Personnage central qui conduit le récit.",
      },
      objective: {
        name: "Objectif",
        description: "But clair et mesurable du protagoniste.",
      },
      antagonist: {
        name: "Antagoniste / Force",
        description: "Force qui empêche la progression du protagoniste.",
      },
      conflict: {
        name: "Conflit",
        description: "Tension principale de l'histoire.",
      },
      stakes: {
        name: "Enjeux (Stakes)",
        description: "Ce qui est en jeu si le protagoniste échoue.",
      },
      setting: {
        name: "Décor / Époque",
        description: "Où et quand l'histoire se déroule.",
      },
      theme: {
        name: "Thème",
        description: "Idée centrale ou argument de l'œuvre.",
      },
      internal_desire: {
        name: "Désir Interne",
        description: "Besoin émotionnel du protagoniste.",
      },
      tragic_flaw: {
        name: "Faille Tragique",
        description: "Défaut qui génère le conflit.",
      },
      arc: {
        name: "Arc de Personnage",
        description: "Transformation tout au long de l'histoire.",
      },
      inciting_incident: {
        name: "Incident Déclencheur",
        description: "Événement qui lance le voyage.",
      },
      plan: {
        name: "Plan du Protagoniste",
        description: "Stratégie pour atteindre l'objectif.",
      },
      obstacles: {
        name: "Obstacles Progressifs",
        description: "Difficultés croissantes.",
      },
      no_return: {
        name: "Point de Non-Retour",
        description: "Décision irréversible.",
      },
      climax: { name: "Climax", description: "Confrontation maximale." },
      resolution: {
        name: "Résolution",
        description: "Nouvel équilibre final.",
      },
      motif: {
        name: "Motif Récurrent",
        description: "Élément symbolique répété.",
      },
      irony: {
        name: "Ironie Dramatique",
        description: "Contraste entre intention et résultat.",
      },
      moral_cost: {
        name: "Règle Morale / Coût Éthique",
        description: "Prix de la victoire.",
      },
      genre_promise: {
        name: "Promesse de Genre",
        description: "Attente créée chez le public.",
      },
      tone: { name: "Ton", description: "Attitude émotionnelle du récit." },
      pacing: {
        name: "Rythme / Escalada",
        description: "Progression de la tension.",
      },
      trigger: {
        name: "Déclencheur Émotionnel",
        description: "Émotion principale provoquée chez le public.",
      },
      plant_payoff: {
        name: "Plant & Payoff",
        description: "Préparation et récompense narrative.",
      },
      pov: {
        name: "Point de Vue Narratif",
        description: "Qui conduit émotionnellement.",
      },
      world_conflict: {
        name: "Monde en Conflit",
        description: "Comment le monde réagit.",
      },
      ending_feeling: {
        name: "Fin Émotionnelle",
        description: "Sensation laissée au public.",
      },
    },
    pillar_protagonist_placeholder: "Qui est le héros ? (Nom, Âge, Rôle)",
    pillar_goal_placeholder: "Que veut-il ? (Objectif tangible)",
    pillar_antagonist_placeholder:
      "Qui/quoi empêche le protagoniste ? [personne, système, nature, soi-même]",
    pillar_conflict_placeholder: "Dynamique du conflit dramatique central.",
    pillar_stakes_placeholder:
      "Que perdent-ils s'ils échouent ? Conséquences externes et internes.",
    pillar_setting_placeholder: "Où et Quand ? (Règles du monde)",
    apply_pillars: "Appliquer Piliers",
    auto_suggest: "Auto-Suggérer",
    define_pillars: "Definir Piliers",
    analyzing_pillars: "Analyse de l'ADN du scénario...",
    detected_format: "Format Détecté",
    format_film: "Film",
    format_animation: "Animation",
    format_documentary: "Documentaire",
    format_advertisement: "Publicité",
    add_pillars: "Ajouter des Piliers",
    add_to_chat_btn: "Ajouter au Chat",
    regenerate_all: "Tout régénérer",

    // Project Creation Modal
    start_script_modal_title: "Commencer Nouveau Scénario",
    edit_title_page: "Modifier la Page de Titre",
    project_title_label: "Titre Provisoire",
    author_label: "Nom de l'Auteur",
    based_on_label: "Basé sur, S'il y a lieu",
    contact_info_label: "E-mail / Téléphone",
    written_by_label: "Écrit par",
    create_project_btn: "Créer le Projet",
    recategorize: "Recatégoriser",
    move_to_existing: "Déplacer vers une catégorie existante",
    create_new_category: "Créer une nouvelle catégorie",
    new_category_name: "Nom de la nouvelle catégorie",
    category: "Catégorie",
    ignore_element: "Ignorer l'Élément",
    elements_footer_msg: "Générez un scénario pour activer les éléments",
    auth_errors: {
      invalid_email: "Format d'e-mail invalide.",
      user_disabled: "Ce compte utilisateur a été désactivé.",
      user_not_found: "E-mail ou mot de passe incorrect.",
      wrong_password: "E-mail ou mot de passe incorrect.",
      email_already_in_use: "Cet e-mail est déjà utilisé. Essayez de vous connecter.",
      account_exists_with_different_credential: "Un compte existe déjà avec cet e-mail utilisant une méthode de connexion différente.",
      invalid_credential: "E-mail ou mot de passe incorrect.",
      weak_password: "Le mot de passe est trop faible (min 6 caractères).",
      operation_not_allowed: "Opération de connexion non autorisée.",
      too_many_requests: "Trop de tentatives échouées. Réessayez plus tard.",
      default: "Une erreur inattendue s'est produite. Veuillez réessayer.",
    },
    email: "E-mail",
    confirm_email: "Confirmer l'e-mail",
    password: "Mot de passe",
    email_mismatch: "Les e-mails ne correspondent pas.",
    forgot_password_question: "Mot de passe oublié ?",
    no_account_link: "Vous n'avez pas de compte ?",
    signup_now: "S'inscrire",
    already_have_account_link: "Vous avez déjà un compte ?",
    login_now: "Se connecter",
    check_email_title: "Vérifiez votre e-mail",
    recovery_sent_msg: "Nous avons envoyé un lien de récupération à :",
    back_to_login: "Retour à la connexion",
    send_link: "Envoyer le lien",

    // Character Modal
    char_modal_title: "Profil du Personnage",
    dramatic_function: "Fonction Dramatique",
    char_name_placeholder: "NOM DU PERSONNAGE",
    identity: "Identité",
    identity_fullname_placeholder: "Ex: Jean Dupont",
    identity_age_placeholder: "Ex: 35 ans",
    identity_profession_placeholder: "Ex: Détective Privé",
    identity_social_class_placeholder: "Ex: Classe Moyenne",
    identity_era_placeholder: "Ex: Londres, 1890",
    identity_location_placeholder: "Ex: Rue Baker",
    psychology: "Psychologie",
    psychology_archetype_placeholder: "Ex: Héros Réticent",
    psychology_moral_placeholder: "Décrivez les valeurs et principes...",
    psychology_fears_placeholder: "Ce que le personnage craint...",
    psychology_goal_placeholder: "La motivation principale dans l'histoire...",
    expression: "Expression",
    expression_speech_placeholder: "Ex: Formel, spirituel, sarcastique...",
    expression_humor_placeholder: "Ex: Humour noir, ironique, aucun...",
    expression_visual_placeholder: "Prompt détaillé pour la génération d'images...",
    full_name: "Nom Complet",
    age: "Âge",
    profession: "Profession/Rôle",
    social_class: "Classe Sociale",
    era_setting: "Époque/Décor",
    location: "Emplacement",
    real_person: "Personne Réelle ?",
    archetype: "Archétype",
    personality_traits: "Traits de Personnalité",
    moral_code: "Code Moral / Position",
    fears: "Peurs / Douleurs",
    primary_goal: "Objectif Principal",
    speech_style: "Style de Parole",
    topics: "Sujets Fréquents",
    humor: "Sens de l'Humour",
    visual_desc: "Description Visuelle (Prompt de l'Avatar)",
    save_char: "Enregistrer le Personnage",
    role_protagonist: "Protagoniste",
    role_antagonist: "Antagoniste",
    role_supporting: "Secondaire",
    role_extra: "Figurant",
    role_interviewee: "Interviewé",
    role_narrator: "Narrateur",
    role_mascot: "Mascotte",
    import_characters: "Importer des Personnages",
    import_characters_desc: "Extraire les profils du scénario",
    char_modal_extract: "EXTRACTION DE PERSONNAGE",
    analysis_complete_verify: "Analyse Terminée - Vérifiez les détails",
    all_chars_have_profiles: "Tous les personnages détectés ont déjà des profils.",
    ai_extraction_results: "Personnages Identifiés",
    review_candidates: "Sélectionnez les personnages à importer.",
    deselect_all: "Tout Désélectionner",
    select_all: "Tout Sélectionner",
    confirm_import: "Importer",
    character_profile_not_extracted: "Profil pas encore extrait. Remplissez manuellement ou utilisez l'Extraction Automatique dans la barre latérale.",
    char_extracting: "EXTRACTION DE PERSONNAGE",
    auto_complete_profile: "✨ Auto-Compléter le Profil",
    profile_auto_filled: "Profil de {name} rempli par l'IA.",

    // Audit
    technical_error: "ERREUR TECHNIQUE",
    stylistic_suggestion: "SUGG. STYLISTIQUE",
    what_is_it: "Qu'est-ce que c'est ?",
    technical_error_tip: "Violations des règles Master Scenes qui empêchent le scénario d'être filmable ou professionnel (déduit des points)",
    stylistic_suggestion_tip: "Suggestions de sous-texte et de raffinement créatif (N'affecte PAS le score technique)",
    selected: "sélectionnés",
    ignore: "Ignorer",
    applied: "Appliqué",
    ignored: "Ignoré",

    // Round / Status
    final_round: "Tour Final",
    round_label: "Tour",
    verified: "Vérifié",

    // Character Diagnostic Paths
    path_a: "Chemin A : Ajuster le Scénario",
    path_b: "Chemin B : Mettre à jour le Profil",
    or: "Ou...",

    // Paramètres
    mature_language: "Contenu Mature",
    mature_language_desc: "Activer le contenu non filtré (jurons, violence, thèmes adultes).",
    horror_mode: "Mode Horreur & Violence",
    horror_mode_desc: "activer une tension extrême, du gore et des descriptions viscérales.",
  },
};
